import { API_BASE_URL, DEFAULT_ORG_SLUG } from "./config.js";
import type {
  AssessmentValidation,
  SittingValidation,
} from "@exambase/shared";
import type {
  PlatformBrand,
  PlatformBrandingStored,
  PlatformBrandDefaults,
} from "./lib/brandRuntime.js";
import {
  getAccessToken,
  setAccessToken,
  getActiveOrgSlug,
  clearSession,
} from "./auth.js";
import { recordResponse } from "./lib/requestTrace.js";

function buildAuthHeaders(): Record<string, string> {
  const slug = getActiveOrgSlug() ?? DEFAULT_ORG_SLUG;
  const token = getAccessToken();
  const h: Record<string, string> = {};
  if (slug) h["x-organisation-slug"] = slug;
  if (token) h["authorization"] = `Bearer ${token}`;
  return h;
}

/**
 * fetch for RAW-BODY endpoints (audio, recording chunks, blob downloads), with
 * the same expired-token recovery `request()` gives every JSON call.
 *
 * Why this exists: these endpoints used to call fetch() directly, so a 401 was
 * final. The access token lives 15 minutes and a speaking exam runs 20, so the
 * token reliably died mid-exam. Every JSON call refreshed itself and carried
 * on, which is why the page looked healthy, while the candidate's recording
 * upload 401'd and showed "your session may have expired" — and "Retry upload"
 * re-sent with the same dead token, so it could never recover. The same flaw
 * silently truncated invigilation replay chunks and could stop a listening
 * paper's audio loading partway through.
 *
 * Auth headers are rebuilt AFTER the refresh, not reused, or the retry would
 * carry the same expired bearer. Bodies here are Blobs or absent, both
 * re-sendable; do not pass a stream, which cannot be replayed on the retry.
 */
export async function authedFetch(
  path: string,
  init: RequestInit & { headers?: Record<string, string>; timeoutMs?: number } = {},
): Promise<Response> {
  const { timeoutMs, ...rest } = init;
  // Each ATTEMPT gets its own deadline. A single outer AbortController would
  // keep counting through the refresh, so a retry could inherit an
  // already-fired signal and reject with an AbortError instead of the
  // ApiError the caller's error mapping expects.
  const send = () =>
    fetch(`${API_BASE_URL}${path}`, {
      ...rest,
      ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
      headers: { ...buildAuthHeaders(), ...(init.headers ?? {}) },
      credentials: "include",
    });
  const res = await send();
  recordResponse(path, res);
  if (res.status !== 401) return res;
  // One refresh, one retry. `request()` bounces to /login when the refresh
  // itself fails; deliberately not done here — losing an in-progress exam
  // recording to a redirect is worse than surfacing the error, and the caller
  // already renders a re-try affordance that keeps the audio in the page.
  if (!(await refresh())) return res;
  const retried = await send();
  recordResponse(path, retried);
  return retried;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    /**
     * Parsed from the `retry-after` response header on a 429. Callers that
     * back off (the intake autosave loop) must honour the server's own number
     * rather than guessing a constant, or a client that retries too eagerly
     * lands straight back in the same fixed window.
     */
    public retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

export interface IntakeSnapshot {
  organisation_name: string;
  contact_email: string;
  status: "draft" | "submitted" | "applied";
  answers: Record<string, unknown>;
  expires_at: string;
  submitted_at: string | null;
}

// ── Email template console ──
export interface EmailTemplateCopy {
  subject: string;
  preheader: string;
  heading: string;
  paragraphs: string[];
  cta_label?: string;
  note?: string;
}
export type EmailTemplateCopyPatch = Partial<EmailTemplateCopy>;
export interface EmailTemplateSummary {
  id: string;
  name: string;
  description: string;
  audience: "candidate" | "staff" | "platform";
  centre_scoped: boolean;
  tokens: string[];
  sample: Record<string, string>;
  default: EmailTemplateCopy;
  copy: EmailTemplateCopy;
  overridden: boolean;
}

/** Exactly what Apply will write, so the admin approves the real change set. */
export interface IntakeApplyPreview {
  centreDoc: Record<string, string>;
  branding: Record<string, string>;
  transcript: Record<string, string | number | boolean>;
}

export interface IntakeAdminRow {
  id: string;
  contact_email: string;
  status: string;
  answers: Record<string, unknown>;
  expires_at: string;
  /** Second, independent predicate from status: revoke and apply both expire the row. */
  live: boolean;
  last_saved_at: string | null;
  submitted_at: string | null;
  applied_at: string | null;
  created_at: string;
}

// Build a query string from a flat filter object, skipping null/empty/false.
function toQueryString(filter: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v == null || v === "" || v === false) continue;
    p.append(k, String(v));
  }
  return p.toString();
}

// Progressive session-replay chunk fetch. The server returns ONE chunk's rrweb
// event array as plain JSON. A missing chunk (404) is returned as [] and skipped
// (rrweb resyncs at the next Meta+FullSnapshot); a real error THROWS so the
// player can surface "N segments failed" rather than a silent empty replay.
//
// Raw body, so it goes through authedFetch for the 401 refresh — the access
// token is a 15-min JWT and a replay can be opened on a long-lived tab.
// Without it an expired token 401s every chunk, all are swallowed, and the
// player shows nothing despite a valid recording. refresh() is single-flighted,
// so a burst of concurrent 401s collapses to one refresh.
async function fetchReplayChunk(path: string): Promise<unknown[]> {
  const res = await authedFetch(path);
  if (!res.ok) {
    if (res.status === 404) return [];
    throw new ApiError(res.status, "internal", `HTTP ${res.status}`);
  }
  const data = await res.json().catch(() => [] as unknown[]);
  return Array.isArray(data) ? data : [];
}

async function request<T>(
  path: string,
  init: RequestInit & { retryOn401?: boolean } = {},
): Promise<T> {
  const slug = getActiveOrgSlug() ?? DEFAULT_ORG_SLUG;
  const headers = new Headers(init.headers);
  // Default to JSON, but leave a binary body's content-type intact (raw
  // file uploads like the asset /binary endpoint set their own type).
  const bodyIsBinary = init.body != null && typeof init.body !== "string";
  if (!bodyIsBinary && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (slug) headers.set("x-organisation-slug", slug);
  const token = getAccessToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  // `credentials: "include"` is required so the browser attaches the
  // HttpOnly `eb_refresh` cookie on calls to /v1/auth/refresh and
  // /v1/auth/logout. Other endpoints don't read the cookie (it's
  // path-scoped to /v1/auth) but enabling credentials globally avoids
  // a per-call branch and is harmless for non-auth routes.
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  recordResponse(path, res);

  if (res.status === 401 && init.retryOn401 !== false) {
    const refreshed = await refresh();
    if (refreshed) {
      return request<T>(path, { ...init, retryOn401: false });
    }
    // Refresh failed (cookie missing, expired, or revoked). Wipe the
    // local profile so the next mount doesn't loop trying to hydrate a
    // ghost session, then bounce to /login. Skip the redirect on
    // routes that already sit outside the authenticated shell so
    // /verify, /pricing, etc. don't hijack a logged-out visitor.
    // NEVER evict a candidate who is mid-attempt. The exam runner holds the
    // in-flight recording in memory only, so a navigation destroys work that
    // is still recoverable, and refresh() returns false for a TRANSIENT
    // failure too (a 429 from the refresh limiter, a 5xx, a dropped
    // connection). An exam hall NATs to one IP and its candidates all start
    // together, so their token expiries land in the same minute and can trip
    // that per-IP limit at once: one blip would otherwise log out the whole
    // room mid-exam. The page surfaces the dead session itself and keeps the
    // retry affordances that still hold the audio.
    const onAttempt =
      typeof window !== "undefined" &&
      /^\/attempts\/[^/]+\/run\b/.test(window.location.pathname);
    if (onAttempt) {
      throw new ApiError(
        401,
        "unauthenticated",
        "Your session could not be renewed.",
      );
    }
    clearSession();
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const publicPaths = [
        "/login",
        "/signup",
        "/enter",
        "/forgot-password",
        "/reset-password",
        "/verify",
        "/pricing",
        "/trust",
        "/privacy",
        "/terms",
        "/cookies",
        "/dpa",
        "/accessibility",
        "/status",
        // The pre-onboarding intake is filled in by someone with no account.
        // Bouncing them to a login they cannot use would lose the form.
        "/intake",
        "/",
      ];
      const onPublic =
        publicPaths.includes(path) ||
        publicPaths.some((p) => p !== "/" && path.startsWith(p + "/"));
      if (!onPublic) {
        window.location.assign("/login");
      }
    }
  }

  if (!res.ok) {
    const body = await safeJson(res);
    const err = body?.error ?? {};
    const retryAfter = Number(res.headers.get("retry-after"));
    throw new ApiError(
      res.status,
      err.code ?? "internal",
      err.message ?? res.statusText,
      err.details,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Module-level in-flight guard. Without this, N concurrent 401s (e.g.
// the `/auth/me` + `/platform/questions` + `/org/programmes` trio fired
// on boot) each call refresh() against the same cookie. The server
// rotates the cookie on the first success — the 2nd and 3rd calls then
// arrive with a stale cookie value (the browser hasn't seen the
// Set-Cookie response yet), fail, and clear the session. Single-
// flighting collapses concurrent callers onto one fetch.
let inFlightRefresh: Promise<boolean> | null = null;

// Single-flighting only collapses CONCURRENT callers, so a genuinely dead
// session still pays a full round trip per 401. The session recorder flushes a
// chunk every 10 seconds for the rest of the exam, so one candidate in that
// state would fire several /v1/auth/refresh POSTs a minute. That endpoint is
// rate-limited per IP, and an exam hall is one IP: a handful of candidates
// could saturate the window and start failing refreshes for everyone in the
// room, including those whose token had merely expired normally. After a
// failure, stop asking for a bit.
const REFRESH_BACKOFF_MS = 30_000;
let refreshDeadUntil = 0;

/** Test seam: the backoff is module state and would leak between cases. */
export function __resetRefreshBackoff(): void {
  refreshDeadUntil = 0;
}

// Same pattern for /v1/auth/me, which UserMenu + Dashboard + the
// ImpersonationBanner all fetch on mount. Without coalescing we pay
// for the round-trip 2–3 times every time the authed shell mounts.
let inFlightMe: Promise<SharedMeResponse> | null = null;

// Refresh the access token using the HttpOnly refresh cookie. No body —
// the cookie travels with the request thanks to `credentials: "include"`.
// Exported so raw-fetch helpers (downloadAuthed) can share the same
// single-flighted refresh instead of 401ing on an idle tab.
export async function refresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  if (Date.now() < refreshDeadUntil) return false;

  inFlightRefresh = (async () => {
    try {
      // Send the active org so the refreshed token stays bound to the tenant
      // the client is using. A multi-tenant session is minted UNSCOPED at login
      // (the user picks an org after), so without this a page reload would
      // refresh back to an unscoped token and 403 every org call.
      const slug = getActiveOrgSlug();
      const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(slug ? { "x-organisation-slug": slug } : {}),
        },
        credentials: "include",
      });
      if (!res.ok) {
        refreshDeadUntil = Date.now() + REFRESH_BACKOFF_MS;
        return false;
      }
      const body = (await res.json()) as {
        access_token: string;
        access_expires_in: number;
      };
      setAccessToken(body.access_token);
      refreshDeadUntil = 0;
      return true;
    } catch {
      // Network failure backs off too: a dropped connection retried every
      // 10 seconds by the chunk flusher is the same storm.
      refreshDeadUntil = Date.now() + REFRESH_BACKOFF_MS;
      return false;
    } finally {
      // Clear the guard in a microtask so any awaiting callers that
      // chain off the returned promise resolve before we null it out.
      // Subsequent 401s (minutes from now) get a fresh guard.
      queueMicrotask(() => {
        inFlightRefresh = null;
      });
    }
  })();

  return inFlightRefresh;
}

type ErrorEnvelope = {
  error?: { code?: string; message?: string; details?: unknown };
};

async function safeJson(res: Response): Promise<ErrorEnvelope | null> {
  try {
    return (await res.json()) as ErrorEnvelope;
  } catch {
    return null;
  }
}

// LoginResponse used to live here as a hand-maintained interface. It now
// comes from the shared Zod schema in @exambase/shared/api — runtime
// validation is applied at the call sites below so a back-end shape
// change fails loudly in dev instead of silently corrupting the UI.
import {
  loginResponseSchema,
  registerResponseSchema,
  meResponseSchema,
  profileResponseSchema,
  type LoginResponse as SharedLoginResponse,
  type RegisterResponse as SharedRegisterResponse,
  type MeResponse as SharedMeResponse,
  type ProfileResponse as SharedProfileResponse,
} from "@exambase/shared/api";
export type LoginResponse = SharedLoginResponse;
export type RegisterResponse = SharedRegisterResponse;
export type MeResponse = SharedMeResponse;
export type ProfileResponse = SharedProfileResponse;

import type {
  SubmissionListResult,
  SubmissionSummary,
  SubmissionDetail,
  SubmissionTimelineEvent,
  SubmissionEvidenceManifest,
} from "@exambase/shared";

// Shared query-string builder for all three submissions endpoints.
// Array values (e.g. status[]) are appended individually; null/empty
// values are skipped. Returns the serialised query string (no leading "?").
function submissionsQuery(filter: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v == null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
    else qs.set(k, String(v));
  }
  return qs.toString();
}

export const api = {
  // Explicit manual refresh — callable from the session-expiry warning toast.
  // The internal refresh() is also used automatically by request() on 401.
  refreshSession: (): Promise<boolean> => refresh(),

  login: async (
    body: { email: string; password: string; org_slug?: string },
    turnstileToken?: string | null,
  ): Promise<LoginResponse> => {
    const raw = await request<unknown>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
      headers: turnstileToken ? { "cf-turnstile-token": turnstileToken } : {},
    });
    return loginResponseSchema.parse(raw);
  },
  register: async (
    body: {
      email: string;
      password: string;
      full_name: string;
      org_name: string;
      org_slug: string;
    },
    turnstileToken?: string | null,
  ): Promise<RegisterResponse> => {
    const raw = await request<unknown>("/v1/auth/register", {
      method: "POST",
      body: JSON.stringify(body),
      headers: turnstileToken ? { "cf-turnstile-token": turnstileToken } : {},
    });
    return registerResponseSchema.parse(raw);
  },
  me: (): Promise<MeResponse> => {
    if (inFlightMe) return inFlightMe;
    inFlightMe = request<unknown>("/v1/auth/me")
      .then((raw) => meResponseSchema.parse(raw))
      .finally(() => {
        // Clear in a microtask so any awaiting callers that chain off
        // the returned promise resolve before we null the guard.
        queueMicrotask(() => {
          inFlightMe = null;
        });
      });
    return inFlightMe;
  },

  bulkUpdateMembers: (body: {
    user_ids: string[];
    action: "suspend" | "unsuspend" | "set_role";
    role?: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
  }) =>
    request<{
      total: number;
      succeeded: number;
      failed: number;
      results: Array<{ user_id: string; ok: boolean; error?: string }>;
    }>(`/v1/org/members/bulk`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Public tenant branding (no auth). Used by the login page to render
  // tenant colours + logo when the user's email domain or slug hint
  // identifies which org they're signing into.
  checkSlugAvailable: (slug: string) =>
    request<{ available: boolean; reason?: "invalid" | "taken" }>(
      `/v1/public/slug-available/${encodeURIComponent(slug)}`,
    ),

  getPublicBrand: (slug: string) =>
    request<{
      logo_r2_key: string | null;
      primary_hsl: string | null;
      accent_hsl: string | null;
    }>(`/v1/public/brand/${slug}`),

  // ---- Platform rebranding -------------------------------------------------
  // Public platform brand (no auth) — fetched once at boot to theme the app.
  getPlatformBrand: () => request<PlatformBrand>(`/v1/public/platform-brand`),
  // Super-admin config (read raw stored values + built-in defaults for the form).
  // ── Email template console (super-admin) ──
  listEmailTemplates: () =>
    request<{ templates: EmailTemplateSummary[] }>(
      `/v1/platform/email-templates`,
    ),
  previewEmailTemplate: (id: string, copy: EmailTemplateCopyPatch) =>
    request<{
      subject: string;
      html: string;
      text: string;
      errors: string[];
    }>(`/v1/platform/email-templates/${id}/preview`, {
      method: "POST",
      body: JSON.stringify(copy),
    }),
  saveEmailTemplate: (id: string, copy: EmailTemplateCopyPatch) =>
    request<{ copy: EmailTemplateCopy; overridden: boolean }>(
      `/v1/platform/email-templates/${id}`,
      { method: "PUT", body: JSON.stringify(copy) },
    ),
  resetEmailTemplate: (id: string) =>
    request<{ copy: EmailTemplateCopy; overridden: boolean }>(
      `/v1/platform/email-templates/${id}`,
      { method: "DELETE" },
    ),

  getPlatformBranding: () =>
    request<{
      branding: PlatformBrandingStored;
      defaults: PlatformBrandDefaults;
    }>(`/v1/platform/branding`),
  setPlatformBranding: (patch: Partial<PlatformBrandingStored>) =>
    request<{
      branding: PlatformBrandingStored;
      defaults: PlatformBrandDefaults;
    }>(`/v1/platform/branding`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  uploadPlatformBrandAsset: async (kind: "logo" | "icon" | "cert_logo", file: File) => {
    const headers: Record<string, string> = { "content-type": file.type };
    const res = await authedFetch(
      `/v1/platform/branding/asset/${kind}`,
      { method: "POST", headers, body: file },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "upload_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return (await res.json()) as { ok: true; kind: string; r2_key: string };
  },
  deletePlatformBrandAsset: (kind: "logo" | "icon" | "cert_logo") =>
    request<{ ok: true; kind: string }>(
      `/v1/platform/branding/asset/${kind}`,
      { method: "DELETE" },
    ),

  // Global search — super_admin gets tenants + users across the platform,
  // tenant-scoped users get assessments + members in their active org.
  platformSearch: (q: string) =>
    request<{
      hits: Array<{
        kind: "tenant" | "user" | "assessment" | "member";
        id: string;
        label: string;
        sublabel: string | null;
        to: string;
      }>;
    }>(`/v1/platform/search?q=${encodeURIComponent(q)}`),
  tenantSearch: (q: string) =>
    request<{
      hits: Array<{
        kind: "tenant" | "user" | "assessment" | "member";
        id: string;
        label: string;
        sublabel: string | null;
        to: string;
      }>;
    }>(`/v1/search?q=${encodeURIComponent(q)}`),

  // Profile & account self-service
  getProfile: async (): Promise<ProfileResponse> => {
    const raw = await request<unknown>("/v1/org/me/profile");
    return profileResponseSchema.parse(raw);
  },
  updateProfile: (body: { full_name?: string; locale?: string }) =>
    request<{ ok: true; updated: number }>("/v1/org/me/profile", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  changePassword: (body: {
    current_password: string;
    new_password: string;
  }) =>
    request<{ ok: true }>("/v1/org/me/change-password", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Avatar — raw binary upload, authed-only serve. Mirrors the branding
  // logo pattern. Only the owner can read their own avatar bytes.
  uploadAvatar: async (file: File) => {
    const res = await authedFetch(`/v1/org/me/avatar`, {
      method: "POST",
      headers: { "content-type": file.type },
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "upload_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return (await res.json()) as { ok: true; avatar_r2_key: string };
  },
  deleteAvatar: () =>
    request<{ ok: true }>("/v1/org/me/avatar", { method: "DELETE" }),

  // Fetch the current user's avatar bytes WITH auth — the serve endpoint is
  // owner-only, and a plain <img src> can't carry the bearer token. Returns an
  // object URL to render (caller revokes it), or null when there's no avatar.
  getMeAvatarUrl: async (): Promise<string | null> => {
    const headers: Record<string, string> = {};
    const res = await fetch(`${API_BASE_URL}/v1/org/me/avatar`, {
      headers,
      credentials: "include",
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  },

  // Email change — two-step. /initiate mints a token and dispatches a
  // confirmation email to the NEW address; /confirm (public) redeems it.
  initiateEmailChange: (body: { password: string; new_email: string }) =>
    request<{ ok: true }>("/v1/org/me/email-change", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  pendingEmailChange: () =>
    request<{
      pending: {
        id: string;
        new_email: string;
        requested_at: string;
        expires_at: string;
      } | null;
    }>("/v1/org/me/email-change/pending"),
  cancelEmailChange: () =>
    request<{ ok: true }>("/v1/org/me/email-change/cancel", {
      method: "POST",
    }),
  confirmEmailChange: async (token: string) => {
    const res = await fetch(
      `${API_BASE_URL}/v1/public/email-changes/${encodeURIComponent(token)}/confirm`,
      { method: "POST" },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "confirm_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return (await res.json()) as { ok: true; new_email: string };
  },

  // Email verification. confirm is PUBLIC (the link in the signup email is the
  // auth factor) so it bypasses request()'s auth header, mirroring
  // confirmEmailChange. resend is authed — it rides the normal access token and
  // is reachable even while the verify gate has the user otherwise locked.
  confirmEmailVerification: async (token: string) => {
    const res = await fetch(
      `${API_BASE_URL}/v1/public/email-verifications/${encodeURIComponent(token)}/confirm`,
      { method: "POST" },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "confirm_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return (await res.json()) as { ok: true };
  },
  resendVerification: () =>
    request<{ ok: true }>("/v1/auth/resend-verification", { method: "POST" }),

  logout: () =>
    // No body — the API reads the eb_refresh cookie. credentials: "include"
    // is set by request() so the cookie travels.
    request<{ ok: true }>("/v1/auth/logout", { method: "POST" }),

  switchOrg: (org_slug: string) =>
    // The server rotates the eb_refresh cookie inline; only the new access
    // token comes back in the body.
    request<{
      active_organisation_id: string;
      access_token: string;
      access_expires_in: number;
    }>("/v1/auth/switch-org", {
      method: "POST",
      body: JSON.stringify({ org_slug }),
    }),

  listAssessments: (opts?: {
    module?: string;
    status?: string;
    programme?: "fs_english" | "heda";
    templates?: "include" | "only";
    // Keyset ("Load more") pagination. Callers that don't pass a cursor get
    // the first page; next_cursor is null once the list is exhausted.
    cursor?: string | null;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.module) qs.set("module", opts.module);
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.programme) qs.set("programme", opts.programme);
    if (opts?.templates) qs.set("templates", opts.templates);
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const q = qs.toString() ? `?${qs}` : "";
    return request<{
      assessments: Array<AssessmentSummary>;
      next_cursor: string | null;
    }>(`/v1/assessments${q}`);
  },

  markAssessmentTemplate: (id: string, value: boolean) =>
    request<{ ok: true; is_template: boolean }>(
      `/v1/assessments/${id}/template?value=${value}`,
      { method: "POST" },
    ),

  getTranscriptTemplate: (programmeId: string) =>
    request<TranscriptTemplate>(
      `/v1/org/programmes/${encodeURIComponent(programmeId)}/transcript-template`,
    ),

  updateTranscriptTemplate: (
    programmeId: string,
    body: Partial<TranscriptTemplate>,
  ) =>
    request<TranscriptTemplate>(
      `/v1/org/programmes/${encodeURIComponent(programmeId)}/transcript-template`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  listOrgProgrammes: () =>
    request<{
      programmes: Array<{
        id: string;
        slug: "fs_english" | "heda";
        name: string;
        subject_labels: string[];
        level_codes: string[];
        is_active: boolean;
      }>;
    }>("/v1/org/programmes"),

  listPlatformProgrammes: () =>
    request<{
      programmes: Array<{
        id: string;
        slug: "fs_english" | "heda";
        name: string;
        subject_labels: string[];
        level_codes: string[];
        is_active: boolean;
      }>;
    }>("/v1/platform/programmes"),

  listProgrammeTenants: (programmeId: string) =>
    request<{
      tenants: Array<{ organisation_id: string; slug: string; name: string }>;
    }>(`/v1/platform/programmes/${programmeId}/tenants`),

  toggleTenantProgramme: (
    programmeId: string,
    body: { organisation_id: string; enabled: boolean },
  ) =>
    request<{ ok: true }>(`/v1/platform/programmes/${programmeId}/toggle`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  toggleAllTenantProgramme: (programmeId: string, enabled: boolean) =>
    request<{ ok: true; affected: number }>(
      `/v1/platform/programmes/${programmeId}/toggle-all`,
      { method: "POST", body: JSON.stringify({ enabled }) },
    ),

  createHedaEnglishAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/heda-english", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createHedaMathAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/heda-math/assessments", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createHedaMathSection: (assessmentId: string, body: CreateReadingSectionBody) =>
    request<{ id: string }>(
      `/v1/heda-math/assessments/${assessmentId}/sections`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  createHedaMathQuestion: (
    sectionId: string,
    body: {
      order_index: number;
      type: "mcq_single" | "mcq_multi" | "true_false" | "numeric_answer";
      prompt: string;
      marks: number;
      options?: Array<{ order_index: number; text: string; is_correct: boolean }>;
      numeric?: {
        value_canonical: string;
        tolerance_absolute?: number | null;
        tolerance_percent?: number | null;
        units?: string | null;
        significant_digits?: number | null;
        marks?: number;
      };
    },
  ) =>
    request<{ id: string }>(
      `/v1/heda-math/sections/${sectionId}/questions`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  createHedaIctAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/heda-ict", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  duplicateAssessment: (id: string) =>
    request<{ id: string }>(`/v1/assessments/${id}/duplicate`, {
      method: "POST",
    }),

  // Cross-tenant assessment template bank: browse platform-curated templates
  // and clone one into your own workspace as a fresh draft.
  listAssessmentTemplates: () =>
    request<{ templates: AssessmentTemplateSummary[] }>(
      "/v1/assessment-templates",
    ),
  adoptAssessmentTemplate: (sourceOrgId: string, assessmentId: string) =>
    request<{ id: string }>(
      `/v1/assessment-templates/${sourceOrgId}/${assessmentId}/adopt`,
      { method: "POST" },
    ),

  // Update adopted questions to the latest bank version (background + revert).
  updateAssessmentQuestions: (assessmentId: string, questionIds?: string[]) =>
    request<{ batch_id: string; question_count: number }>(
      `/v1/question-bank/assessments/${assessmentId}/update-questions`,
      { method: "POST", body: JSON.stringify(questionIds ? { question_ids: questionIds } : {}) },
    ),
  getQuestionUpdateBatch: (batchId: string) =>
    request<QuestionUpdateBatch>(`/v1/question-bank/batches/${batchId}`),
  rollbackQuestionUpdateBatch: (batchId: string) =>
    request<{ ok: true }>(`/v1/question-bank/batches/${batchId}/rollback`, {
      method: "POST",
    }),

  // Tenant-admin: members management
  listOrgMembers: (opts?: { status?: string; q?: string; role?: string }) => {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.q) qs.set("q", opts.q);
    if (opts?.role) qs.set("role", opts.role);
    const q = qs.toString() ? `?${qs}` : "";
    // `total` counts all members matching the same filters (the list itself
    // is capped server-side); `capped` is true when members.length < total.
    return request<{ members: OrgMember[]; total: number; capped: boolean }>(
      `/v1/org/members${q}`,
    );
  },

  // Mints a verification cert for the student's current transcript
  // before they download the PDF. Server snapshots the best-score
  // bands + overall band + org + candidate name; the cert number
  // is printed on the PDF and resolves publicly via /verify/:cert.
  requestAttemptRemark: (attemptId: string, body: { reason: string }) =>
    request<{ ok: true }>(
      `/v1/dashboard/student/attempts/${attemptId}/request-remark`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  verifyOrgAuditChain: () =>
    request<{
      ok: boolean;
      checked: number;
      skipped_unhashed: number;
      broken_at?: {
        id: string;
        created_at: string;
        expected: string | null;
        actual: string | null;
      };
    }>("/v1/org/audit/verify"),

  backfillOrgAuditChain: () =>
    request<{ signed: number; scanned: number }>(
      "/v1/org/audit/backfill?confirm=true",
      { method: "POST" },
    ),

  listOrgAudit: (opts?: {
    action?: string;
    actor?: string;
    since?: string;
    until?: string;
    limit?: number;
    // Keyset ("Load more") pagination. next_cursor is null once the log is
    // exhausted under the active filters.
    cursor?: string | null;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.action) qs.set("action", opts.action);
    if (opts?.actor) qs.set("actor", opts.actor);
    if (opts?.since) qs.set("since", opts.since);
    if (opts?.until) qs.set("until", opts.until);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    const q = qs.toString() ? `?${qs}` : "";
    return request<{
      rows: Array<{
        id: string;
        actor_user_id: string | null;
        // Resolved from the master users table; null for deleted users or
        // a system (null actor) row.
        actor_name: string | null;
        actor_email: string | null;
        action: string;
        target_type: string | null;
        target_id: string | null;
        // Resolved only when target_type === 'user'.
        target_name: string | null;
        target_email: string | null;
        ip: string | null;
        created_at: string;
        metadata: Record<string, unknown>;
      }>;
      next_cursor: string | null;
    }>(`/v1/org/audit${q}`);
  },

  // Candidate right-to-erasure. Requires the caller to pass the
  // deterministic phrase "ERASE <user_id>" so a stray click can't
  // wipe a candidate's record.
  eraseOrgCandidate: (
    userId: string,
    body: { confirm: string; reason?: string },
  ) =>
    request<{
      ok: true;
      attempts_deleted: number;
      responses_deleted: number;
      rubric_scores_deleted: number;
      listening_replays_deleted: number;
      ai_checks_deleted: number;
      progress_rows_deleted: number;
      enrolments_deleted: number;
      membership_removed: boolean;
    }>(`/v1/org/members/${userId}/erase`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateOrgMember: (
    userId: string,
    body: {
      role?: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
      is_examiner?: boolean;
      status?: "active" | "suspended";
    },
  ) =>
    request<{ ok: true }>(`/v1/org/members/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  createOrgInvite: (body: {
    email: string;
    role: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
    is_examiner: boolean;
  }) =>
    request<{ ok: true; invite_id: string }>(`/v1/org/invites`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Pending invites (not yet accepted). These live only in the invites table,
  // so the members list never shows them — the "Invited" tab reads this.
  listOrgInvites: () =>
    request<{ invites: PendingInvite[] }>(`/v1/org/invites`),

  revokeOrgInvite: (id: string) =>
    request<{ ok: true; revoked: boolean }>(`/v1/org/invites/${id}`, {
      method: "DELETE",
    }),

  triggerMemberPasswordReset: (userId: string) =>
    request<{ ok: true }>(`/v1/org/members/${userId}/reset-password`, {
      method: "POST",
    }),

  // Self-service sessions
  listOwnSessions: () =>
    request<{ sessions: OwnSession[] }>(`/v1/org/sessions`),

  revokeOwnSession: (sessionId: string) =>
    request<{ ok: true }>(`/v1/org/sessions/${sessionId}`, {
      method: "DELETE",
    }),

  revokeAllOwnSessions: () =>
    request<{ ok: true }>(`/v1/org/sessions`, { method: "DELETE" }),

  // Public onboarding (no auth)
  getInvite: (token: string) =>
    request<{
      email: string;
      role: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
      is_examiner: boolean;
      organisation_slug: string | null;
      organisation_name: string | null;
      expires_at: string;
      accepted: boolean;
      // False when the address already holds an account with a working
      // password — the accept page then skips the password field.
      needs_password: boolean;
    }>(`/v1/public/invites/${token}`),

  acceptInvite: (
    token: string,
    body: { full_name: string; password?: string },
    turnstileToken?: string | null,
  ) =>
    request<{ user_id: string; organisation_id: string; role: string }>(
      `/v1/public/invites/${token}/accept`,
      {
        method: "POST",
        body: JSON.stringify(body),
        headers: turnstileToken ? { "cf-turnstile-token": turnstileToken } : {},
      },
    ),

  redeemPasswordReset: (
    token: string,
    body: { new_password: string },
    turnstileToken?: string | null,
  ) =>
    request<{ user_id: string }>(`/v1/public/password-resets/${token}/redeem`, {
      method: "POST",
      ...(turnstileToken
        ? { headers: { "cf-turnstile-token": turnstileToken } }
        : {}),
      body: JSON.stringify(body),
    }),

  // --- Candidate Entry Slips ---

  createCandidateSlip: (body: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    role?: "student";
    assessment_id?: string;
    sitting_id?: string;
    exam_centre?: string;
    exam_centre_address?: string;
    exam_date?: string;
    exam_time?: string;
    expires_days?: number;
    max_uses?: number;
    // Optional real contact email (certificates/results); is_test flags a
    // dry-run slip excluded from billing/exports.
    contact_email?: string;
    is_test?: boolean;
  }) =>
    request<{ id: string; access_code: string; expires_at: string }>(
      "/v1/org/candidate-slips",
      { method: "POST", body: JSON.stringify(body) },
    ),

  bulkCreateCandidateSlips: (body: {
    slips: Array<{
      first_name: string;
      last_name: string;
      date_of_birth: string;
      role?: "student";
      assessment_id?: string;
      sitting_id?: string;
      exam_centre?: string;
      exam_centre_address?: string;
      exam_date?: string;
      exam_time?: string;
      expires_days?: number;
      max_uses?: number;
      contact_email?: string;
      is_test?: boolean;
    }>;
  }) =>
    request<{
      slips: Array<{
        id: string;
        first_name: string;
        last_name: string;
        date_of_birth: string;
        access_code: string;
        expires_at: string;
      }>;
      // Rows the server minted no slip for (e.g. a DOB that slipped past
      // client validation). The endpoint reports successes AND failures so a
      // partial import is never silently dropped.
      failed: Array<{ first_name: string; last_name: string; reason: string }>;
    }>("/v1/org/candidate-slips/bulk", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listCandidateSlips: (opts?: {
    status?: "active" | "used" | "revoked" | "expired" | "locked";
    // Comprehensive filters — all optional, ANDed server-side. `q` matches
    // the candidate's first OR last name; `has_photo` "1" = has a photo,
    // "0" = missing; exam_from/exam_to bound the exam_date range.
    q?: string;
    assessment_id?: string;
    sitting_id?: string;
    exam_from?: string;
    exam_to?: string;
    has_photo?: "1" | "0";
    // "1" = has a scheduled exam_date, "0" = unscheduled (the calendar
    // view's Unscheduled bucket).
    has_exam_date?: "1" | "0";
    // "1" = only test slips, "0" = only real slips, absent = both.
    is_test?: "1" | "0";
    // Exact test-centre name match (the slip stores the centre name).
    exam_centre?: string;
    limit?: number;
    // Keyset ("Load more") pagination. next_cursor is null once the
    // (filtered) list is exhausted.
    cursor?: string | null;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.q) qs.set("q", opts.q);
    if (opts?.assessment_id) qs.set("assessment_id", opts.assessment_id);
    if (opts?.sitting_id) qs.set("sitting_id", opts.sitting_id);
    if (opts?.exam_from) qs.set("exam_from", opts.exam_from);
    if (opts?.exam_to) qs.set("exam_to", opts.exam_to);
    if (opts?.has_photo) qs.set("has_photo", opts.has_photo);
    if (opts?.has_exam_date) qs.set("has_exam_date", opts.has_exam_date);
    if (opts?.is_test) qs.set("is_test", opts.is_test);
    if (opts?.exam_centre) qs.set("exam_centre", opts.exam_centre);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    return request<{
      slips: CandidateSlipSummary[];
      next_cursor: string | null;
    }>(`/v1/org/candidate-slips${qs.toString() ? "?" + qs : ""}`);
  },

  // Org-wide aggregate counts for the list summary cards (accurate across all
  // pages, not just the loaded keyset page).
  getCandidateSlipStats: () =>
    request<{
      total: number;
      scheduled: number;
      redeemed: number;
      active: number;
      test: number;
      no_photo: number;
    }>(`/v1/org/candidate-slips/stats`),

  getCandidateSlip: (id: string) =>
    request<CandidateSlipSummary>(`/v1/org/candidate-slips/${id}`),

  revokeCandidateSlip: (id: string) =>
    request<{ ok: true }>(`/v1/org/candidate-slips/${id}/revoke`, {
      method: "POST",
    }),

  reissueCandidateSlip: (id: string) =>
    request<{ id: string; access_code: string }>(
      `/v1/org/candidate-slips/${id}/reissue`,
      { method: "POST" },
    ),

  updateCandidateSlip: (
    id: string,
    body: {
      first_name?: string;
      last_name?: string;
      date_of_birth?: string;
      role?: "student";
      assessment_id?: string | null;
      sitting_id?: string | null;
      exam_centre?: string | null;
      exam_centre_address?: string | null;
      exam_date?: string | null;
      exam_time?: string | null;
      expires_days?: number;
      max_uses?: number;
      // Set/replace the contact email, or explicit null to clear it.
      contact_email?: string | null;
      // Mark/unmark this slip as a test case.
      is_test?: boolean;
    },
  ) =>
    request<{ ok: true }>(`/v1/org/candidate-slips/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // Bulk re-pin / reschedule many slips in one call (e.g. move a cohort onto
  // the correct sitting). Only schedule/pinning fields; identity is never
  // touched. Returns how many rows actually changed (revoked/foreign ids skip).
  bulkUpdateCandidateSlips: (body: {
    ids: string[];
    assessment_id?: string | null;
    sitting_id?: string | null;
    exam_centre?: string | null;
    exam_centre_address?: string | null;
    exam_date?: string | null;
    exam_time?: string | null;
    // Bulk mark/unmark the selected cohort as test-case slips.
    is_test?: boolean;
    // Absolute new expiry date (YYYY-MM-DD), unlike the single edit's
    // "extend by N days" — one shared date is what a cohort can be told.
    expires_at?: string;
    max_uses?: number;
  }) =>
    request<{ ok: true; updated: number }>(
      "/v1/org/candidate-slips/bulk-update",
      { method: "POST", body: JSON.stringify(body) },
    ),

  // Bulk revoke: every listed slip's access code stops working. Idempotent —
  // already-revoked and foreign ids are skipped, and `revoked` is the number
  // that actually changed.
  bulkRevokeCandidateSlips: (ids: string[]) =>
    request<{ ok: true; revoked: number }>(
      "/v1/org/candidate-slips/bulk-revoke",
      { method: "POST", body: JSON.stringify({ ids }) },
    ),

  // --- Candidate slip photo (raw-binary upload, authed-only serve) ---
  // Mirrors the avatar / branding-logo pattern: the body is the raw image
  // Blob (never JSON-encoded) and Content-Type carries the real MIME so the
  // server can re-sniff it. The serve route is authed + sandboxed, so a plain
  // <img src> won't load it — callers must fetch the Blob with these headers.
  uploadCandidateSlipPhoto: async (id: string, file: Blob) => {
    const headers: Record<string, string> = {
      "content-type": file.type || "application/octet-stream",
    };
    const res = await authedFetch(
      `/v1/org/candidate-slips/${id}/photo`,
      { method: "POST", headers, body: file },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "upload_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return { ok: true } as const;
  },
  deleteCandidateSlipPhoto: (id: string) =>
    request<{ ok: true }>(`/v1/org/candidate-slips/${id}/photo`, {
      method: "DELETE",
    }),
  fetchCandidateSlipPhotoBlob: async (id: string): Promise<Blob> => {
    const headers: Record<string, string> = {};
    const res = await authedFetch(
      `/v1/org/candidate-slips/${id}/photo`,
      { method: "GET", headers },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "photo_fetch_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return await res.blob();
  },

  // Fetch the durable certificate photo for an issued cert (Phase 3). Authed +
  // bearer/org-slug headers (mirrors fetchCandidateSlipPhotoBlob); the caller
  // wraps the Blob in a data-URL for the cert/transcript PDF. NEVER public.
  fetchCertificatePhotoBlob: async (certNo: string): Promise<Blob> => {
    const headers: Record<string, string> = {};
    const res = await authedFetch(
      `/v1/dashboard/certificate-photo/${encodeURIComponent(certNo)}`,
      { method: "GET", headers },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "photo_fetch_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return await res.blob();
  },

  candidateEntry: (
    body: { access_code: string; date_of_birth: string },
    turnstileToken?: string | null,
  ) =>
    request<{
      user: { id: string; full_name: string; email: string | null };
      organisation_id: string;
      organisation_slug: string | null;
      organisation_name: string | null;
      role: "student" | "teacher" | "moderator";
      assessment_id: string | null;
      sitting_id: string | null;
      redirect_to: string;
      access_token: string;
      access_expires_in: number;
    }>("/v1/public/candidate-entry", {
      method: "POST",
      body: JSON.stringify(body),
      ...(turnstileToken
        ? { headers: { "cf-turnstile-token": turnstileToken } }
        : {}),
    }),

  // Platform audit (super_admin)
  listPlatformAudit: (opts?: {
    actor?: string;
    action?: string;
    target_type?: string;
    target_id?: string;
    since?: string;
    until?: string;
    limit?: number;
    cursor?: string | null;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.actor) qs.set("actor", opts.actor);
    if (opts?.action) qs.set("action", opts.action);
    if (opts?.target_type) qs.set("target_type", opts.target_type);
    if (opts?.target_id) qs.set("target_id", opts.target_id);
    if (opts?.since) qs.set("since", opts.since);
    if (opts?.until) qs.set("until", opts.until);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    const q = qs.toString() ? `?${qs}` : "";
    return request<{
      events: PlatformAuditEvent[];
      next_cursor: string | null;
    }>(`/v1/platform/audit${q}`);
  },

  platformAuditCsvUrl: (opts?: Record<string, string>) => {
    const qs = new URLSearchParams({ format: "csv" });
    for (const [k, v] of Object.entries(opts ?? {})) if (v) qs.set(k, v);
    return `${API_BASE_URL}/v1/platform/audit?${qs}`;
  },

  // Enterprise — branding (tenant-admin writes)
  getOrgBranding: () => request<TenantBranding>(`/v1/org/branding`),
  setOrgBranding: (patch: Partial<TenantBranding>) =>
    request<TenantBranding>(`/v1/org/branding`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // Bulk actions
  bulkTenantAction: (body: {
    ids: string[];
    action: "suspend" | "unsuspend";
  }) =>
    request<{ ok: true; affected: number }>(`/v1/platform/bulk/tenants`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  uploadPlatformQuestionAsset: async (
    file: File,
    // Optional hint, ONLY used server-side to disambiguate the WebM
    // container (shared audio/video magic). The server sniffs the real
    // bytes and stores the SNIFFED type regardless — this never overrides
    // the allowlist or lets a spoofed payload through.
    kind?: "image" | "audio" | "video" | "any",
  ): Promise<{
    content_hash: string;
    byte_size: number;
    content_type: string;
    url: string;
    // R2 key (question-assets/<hash>.<ext>) — store THIS for audio so
    // adoption → sections.stimulus_r2_key is a key the student listening
    // endpoint can read directly.
    r2_key: string;
  }> => {
    // Raw-body upload to the /binary endpoint — the JSON base64 route is
    // capped at 256 KB by the global body guard, useless for real audio/video.
    // The server sniffs the bytes (never the declared type), so a spoofed
    // payload is still rejected. kind + filename ride on the query string.
    const qs = new URLSearchParams();
    if (kind) qs.set("kind", kind);
    if (file.name) qs.set("filename", file.name);
    return request(`/v1/platform/question-assets/binary?${qs.toString()}`, {
      method: "POST",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
  },

  // Tenant-staff (teacher+) image upload for CERTIFICATE branding + validation
  // signatory images. Same content-addressed store + /v1/question-assets/<hash>
  // read path as uploadPlatformQuestionAsset, but the gate is tenant-scoped so a
  // centre admin / teacher can upload without platform super-admin. Images only.
  uploadCertificateAsset: async (
    file: File,
  ): Promise<{
    content_hash: string;
    byte_size: number;
    content_type: string;
    url: string;
    r2_key: string;
  }> => {
    const qs = new URLSearchParams();
    if (file.name) qs.set("filename", file.name);
    return request(`/v1/org/certificate-assets/binary?${qs.toString()}`, {
      method: "POST",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
  },

  getPlatformQuestionStats: () =>
    request<{
      total: number;
      by_status: Record<string, number>;
      by_module: Record<string, number>;
      by_level: Record<string, number>;
      by_type: Record<string, number>;
      by_quality_bucket: Record<string, number>;
    }>("/v1/platform/questions/stats"),

  // Full structured dump of the question bank (prompt, options,
  // answer key) for the client-side printable PDF booklet.
  exportQuestionBankBooklet: (opts?: {
    status?: string;
    module?: string;
    level_code?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("status", opts?.status ?? "published");
    if (opts?.module) qs.set("module", opts.module);
    if (opts?.level_code) qs.set("level_code", opts.level_code);
    qs.set("limit", String(opts?.limit ?? 5000));
    return request<QuestionBankBooklet>(
      `/v1/platform/questions/export/booklet?${qs.toString()}`,
    );
  },

  // Authenticated CSV/QTI export → Blob. Uses the in-memory access
  // token (auto-refreshed on 401), same as request() — the old
  // hand-rolled localStorage("exambase.tokens") read is dead since the
  // HttpOnly-cookie migration and always 401s.
  exportPlatformQuestions: async (
    fmt: "csv" | "qti",
    params: Record<string, string | number | undefined> = {},
    retry = true,
  ): Promise<Blob> => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    const token = getAccessToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/platform/questions/export/${fmt}?${qs.toString()}`,
      {
        headers: token ? { authorization: `Bearer ${token}` } : {},
        credentials: "include",
      },
    );
    if (res.status === 401 && retry) {
      const ok = await refresh();
      if (ok) return api.exportPlatformQuestions(fmt, params, false);
    }
    if (!res.ok)
      throw new ApiError(
        res.status,
        "export_failed",
        `export failed (${res.status})`,
      );
    return res.blob();
  },

  // Authenticated question-bank CSV import (POST raw text/csv).
  importPlatformQuestionsCsv: async (
    csv: string,
    retry = true,
  ): Promise<{ created: number; duplicates: number; failed: number }> => {
    const token = getAccessToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/platform/questions/import/csv`,
      {
        method: "POST",
        headers: {
          "content-type": "text/csv",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: csv,
      },
    );
    if (res.status === 401 && retry) {
      const ok = await refresh();
      if (ok) return api.importPlatformQuestionsCsv(csv, false);
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: { message?: string };
      };
      throw new ApiError(
        res.status,
        "import_failed",
        body?.error?.message ?? `import failed (${res.status})`,
      );
    }
    return res.json() as Promise<{
      created: number;
      duplicates: number;
      failed: number;
    }>;
  },

  // Trigger the question-quality scanner on demand. The nightly cron
  // runs the same scan; this exists so an admin can refresh scores
  // immediately after a bulk import. Returns { scanned, updated,
  // buckets: { A, B, C, D }, duration_ms }.
  rescanPlatformQuestionQuality: () =>
    request<{
      scanned: number;
      updated: number;
      buckets: { A: number; B: number; C: number; D: number };
      duration_ms: number;
    }>("/v1/platform/questions/quality/rescan", { method: "POST" }),

  listPlatformQuestionDuplicates: (opts?: { limit?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const query = qs.toString() ? `?${qs}` : "";
    return request<{
      groups: Array<{
        hash: string;
        count: number;
        items: Array<{
          id: string;
          prompt: string;
          status: "draft" | "published" | "archived";
          level_code: string;
          module: string;
          type: string;
          created_at: string;
          adopted_count: number;
        }>;
      }>;
      total_groups: number;
      total_items: number;
    }>(`/v1/platform/questions/duplicates${query}`);
  },

  bulkQuestionAction: (body: {
    ids: string[];
    action: "publish" | "archive" | "delete" | "revert" | "submit_review";
    // Required when action === "delete" — server enforces === "DELETE".
    confirm?: string;
  }) =>
    request<{
      requested: number;
      succeeded: number;
      skipped: Array<{ id: string; reason: string }>;
    }>(`/v1/platform/questions/bulk`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Impersonation
  startImpersonation: (body: {
    organisation_id: string;
    reason: string;
    reason_category?:
      | "support_ticket"
      | "incident"
      | "onboarding"
      | "billing"
      | "other";
    view_as_role?: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
    write_mode?: boolean;
    emergency_override?: boolean;
    emergency_justification?: string;
    mfa_code?: string;
  }) =>
    // Refresh token rides on the eb_refresh cookie (rotated by the API);
    // only the access token + impersonation metadata return in the body.
    request<{
      access_token: string;
      access_expires_in: number;
      target_organisation_id: string;
      target_slug: string;
      impersonation_session_id: string;
      view_as_role: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
      view_as_readonly: boolean;
      reason_category: string;
      expires_at: string | null;
    }>(`/v1/platform/impersonate/start`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  endImpersonation: () =>
    request<{ ok: true; sessions_ended: number }>(
      `/v1/platform/impersonate/end`,
      { method: "POST" },
    ),
  getImpersonationCurrent: () =>
    request<{
      session: {
        id: string;
        target_organisation_id: string;
        slug: string;
        name: string;
        reason: string | null;
        started_at: string;
      } | null;
    }>(`/v1/platform/impersonate/current`),
  listImpersonationHistory: (limit = 50) =>
    request<{
      sessions: Array<{
        id: string;
        actor_user_id: string;
        actor_email: string | null;
        target_organisation_id: string;
        slug: string;
        name: string;
        reason: string | null;
        started_at: string;
        ended_at: string | null;
      }>;
    }>(`/v1/platform/impersonate/history?limit=${limit}`),

  // 2FA
  getMfaStatus: () => request<{ enrolled: boolean }>(`/v1/auth/mfa/status`),
  startMfaEnrol: (password: string) =>
    request<{ secret: string; provisioning_uri: string }>(
      `/v1/auth/mfa/enrol`,
      { method: "POST", body: JSON.stringify({ password }) },
    ),
  verifyMfaEnrol: (code: string) =>
    request<{ ok: true; recovery_codes: string[] }>(
      `/v1/auth/mfa/verify-enrol`,
      { method: "POST", body: JSON.stringify({ code }) },
    ),
  disableMfa: (password: string) =>
    request<{ ok: true }>(`/v1/auth/mfa/disable`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  // Login audit (self)
  getOwnLogins: () =>
    request<{
      logins: Array<{
        id: string;
        ip: string | null;
        user_agent: string | null;
        method: string;
        success: number;
        created_at: string;
      }>;
    }>(`/v1/org/me/logins`),

  // GDPR
  requestAccountDeletion: (body: {
    confirm_email: string;
    reason?: string;
  }) =>
    request<{ ok: true; request_id: string }>(
      `/v1/org/me/deletion-request`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  getDeletionRequestStatus: () =>
    request<{
      request: null | {
        id: string;
        reason: string | null;
        requested_at: string;
        status: string;
      };
    }>(`/v1/org/me/deletion-request`),

  // Branding logo
  uploadBrandingLogo: async (file: File) => {
    const headers: Record<string, string> = { "content-type": file.type };
    const res = await authedFetch(`/v1/org/branding/logo`, {
      method: "POST",
      headers,
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        body?.error?.code ?? "upload_failed",
        body?.error?.message ?? res.statusText,
      );
    }
    return (await res.json()) as { ok: true; logo_r2_key: string };
  },
  deleteBrandingLogo: () =>
    request<{ ok: true }>(`/v1/org/branding/logo`, { method: "DELETE" }),

  // Examiner workload
  getMarkingWorkload: () =>
    request<{
      markers: Array<{
        marker_user_id: string;
        // Resolved from the master users table; null for a deleted user.
        marker_name: string | null;
        marker_email: string | null;
        open_count: number;
        completed_30d: number;
        avg_minutes: number | null;
        last_completed_at: string | null;
      }>;
      // Flag examiners with more than this many open sessions. Org setting;
      // defaults to 10 when unset.
      marking_sla_threshold: number;
    }>(`/v1/org/marking-workload`),

  // Tenant operational settings (marking SLA threshold, etc.)
  getOrgSettings: () =>
    request<OrgOpsSettings>(`/v1/org/settings`),
  setOrgSettings: (patch: Partial<OrgOpsSettings>) =>
    request<OrgOpsSettings>(`/v1/org/settings`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // Test centres (exam venues) — a per-org list + default, used to populate the
  // slip's centre picker. GET is readable by any authed tenant member; PUT is
  // tenant_admin (wholesale replace).
  getTestCentres: () =>
    request<TestCentresConfig>(`/v1/org/settings/test-centres`),
  setTestCentres: (config: {
    centres: Array<{ id?: string; name: string; address?: string }>;
    default_id: string | null;
  }) =>
    request<TestCentresConfig>(`/v1/org/settings/test-centres`, {
      method: "PUT",
      body: JSON.stringify(config),
    }),

  // Exam room code: the per-centre location control for candidate entry. A code
  // being SET is what turns enforcement on; clearing it turns it off, so it is
  // never mandatory. All three are slip-manager only (registrar or
  // tenant_admin): the people who run exam day read the code out.
  listRoomCodes: () =>
    request<{ centres: CentreRoomCode[] }>(`/v1/org/settings/room-codes`),
  setRoomCode: (centreId: string, code: string) =>
    request<CentreRoomCode>(
      `/v1/org/settings/room-codes/${encodeURIComponent(centreId)}`,
      { method: "PUT", body: JSON.stringify({ code }) },
    ),
  /** Print handshake: the server rotates any of the named centres' codes that
   *  are older than today (Europe/London) and returns the set to print, so
   *  concurrent prints agree and untouched venues keep their live code. */
  printRoomCodes: (centreIds: string[]) =>
    request<{ centres: CentreRoomCode[] }>(
      `/v1/org/settings/room-codes/print`,
      { method: "POST", body: JSON.stringify({ centre_ids: centreIds }) },
    ),

  // Centre documentation fields (tenant-admin)
  getCentreDocFields: () =>
    request<Record<string, string>>(`/v1/org/centre-doc-fields`),
  setCentreDocFields: (patch: Record<string, string>) =>
    request<Record<string, string>>(`/v1/org/centre-doc-fields`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  // Pre-onboarding intake, public. No account, no session: the token in the
  // path is the only credential, so these must never be called with an
  // organisation id or slug from the client.
  getIntake: (token: string) =>
    request<IntakeSnapshot>(`/v1/public/intake/${encodeURIComponent(token)}`),

  saveIntake: (token: string, patch: Record<string, unknown>) =>
    request<{ saved_at: string; expires_at: string }>(
      `/v1/public/intake/${encodeURIComponent(token)}`,
      { method: "POST", body: JSON.stringify(patch) },
    ),

  submitIntake: (token: string, turnstileToken?: string | null) =>
    request<{ status: "submitted"; submitted_at: string }>(
      `/v1/public/intake/${encodeURIComponent(token)}/submit`,
      {
        method: "POST",
        headers: turnstileToken ? { "cf-turnstile-token": turnstileToken } : {},
      },
    ),

  // Pre-onboarding intake, super admin.
  issueIntake: (orgId: string, contactEmail: string) =>
    request<{ intake_id: string; intake_url: string; expires_at: string }>(
      `/v1/platform/intakes/organisations/${orgId}`,
      { method: "POST", body: JSON.stringify({ contact_email: contactEmail }) },
    ),

  listIntakes: (orgId: string) =>
    request<{ intakes: IntakeAdminRow[] }>(
      `/v1/platform/intakes/organisations/${orgId}`,
    ),

  resendIntake: (intakeId: string) =>
    request<{ intake_url: string; expires_at: string; replaced_previous: true }>(
      `/v1/platform/intakes/${intakeId}/resend`,
      { method: "POST" },
    ),

  revokeIntake: (intakeId: string) =>
    request<{ ok: true }>(`/v1/platform/intakes/${intakeId}/revoke`, {
      method: "POST",
    }),

  // Pre-onboarding intake, tenant admin. This is the ONLY path from the
  // college's answers into live config, and it runs under a real user.
  getPendingIntake: () =>
    request<{
      intake: {
        id: string;
        submitted_at: string | null;
        answers: Record<string, unknown>;
      } | null;
      preview?: IntakeApplyPreview;
    }>(`/v1/org/intake`),

  applyIntake: (intakeId: string) =>
    request<{ applied: string[]; already: boolean }>(`/v1/org/intake/apply`, {
      method: "POST",
      body: JSON.stringify({ intake_id: intakeId }),
    }),

  // Platform deletion requests (super admin)
  listDeletionRequests: () =>
    request<{
      requests: Array<{
        id: string;
        user_id: string;
        email: string;
        full_name: string;
        reason: string | null;
        requested_at: string;
        status: string;
      }>;
    }>(`/v1/platform/deletion-requests`),
  processDeletionRequest: (id: string) =>
    request<{ ok: true; anon_email: string }>(
      `/v1/platform/deletion-requests/${id}/process`,
      { method: "POST" },
    ),
  cancelDeletionRequest: (id: string) =>
    request<{ ok: true }>(`/v1/platform/deletion-requests/${id}/cancel`, {
      method: "POST",
    }),

  // SSO (tenant-admin)
  getOrgSso: () =>
    request<{
      configured: boolean;
      config: null | {
        discovery_url: string;
        client_id: string;
        email_domains: string;
        default_role: string;
        is_active: boolean;
      };
    }>(`/v1/org/sso`),
  setOrgSso: (body: {
    discovery_url: string;
    client_id: string;
    // Optional: omit to keep the existing stored secret (the GET never returns
    // it, so an admin editing a configured connection can't re-type it).
    client_secret?: string;
    email_domains: string;
    default_role: "tenant_admin" | "moderator" | "teacher" | "student";
    is_active: boolean;
  }) =>
    request<{ ok: true }>(`/v1/org/sso`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteOrgSso: () =>
    request<{ ok: true }>(`/v1/org/sso`, { method: "DELETE" }),

  getAssessmentAsStudent: (assessmentId: string) =>
    request<StudentAssessment>(`/v1/assessments/${assessmentId}?as=student`),

  startAttempt: (assessmentId: string) =>
    request<{
      attempt_id: string;
      expires_at: string;
      duration_seconds: number;
      assessment: StudentAssessment;
    }>(`/v1/assessments/${assessmentId}/start`, { method: "POST" }),

  // Teacher "preview as student": the exact student exam bootstrap with a
  // synthetic preview- id, no attempt created. Same shape as startAttempt so
  // the TestRunner renders it identically (in preview mode).
  startAssessmentPreview: (assessmentId: string) =>
    request<{
      attempt_id: string;
      expires_at: string;
      duration_seconds: number;
      assessment: StudentAssessment;
    }>(`/v1/assessments/${assessmentId}/student-preview`, { method: "POST" }),

  getAttempt: (attemptId: string) =>
    request<{
      id: string;
      assessment_id: string;
      student_user_id: string;
      started_at: string;
      submitted_at: string | null;
      expires_at: string;
      status: string;
      score_raw: number | null;
      score_max: number | null;
      score_percent: number | null;
      passed: number | null;
      time_taken_seconds: number | null;
      sitting_id: string | null;
    }>(`/v1/attempts/${attemptId}`),

  // Rebuild the take-page bootstrap (same shape as startAttempt, no new attempt
  // created) for an in-progress attempt — used when the per-tab sessionStorage
  // cache is gone (direct URL, reload, new tab, sitting-hub deep link).
  getAttemptBootstrap: (attemptId: string) =>
    request<{
      attempt_id: string;
      expires_at: string;
      duration_seconds: number;
      assessment: StudentAssessment;
    }>(`/v1/attempts/${attemptId}/bootstrap`),

  // Candidate-side: fetch this attempt's saved responses for
  // rehydration on the take page (e.g. after leave-and-return on a
  // sitting slot). For speaking, has_audio + audio_duration_seconds
  // signal an existing recording so the UI can rebuild the playback
  // widget without forcing the candidate to re-record.
  listOwnResponses: (attemptId: string) =>
    request<{
      responses: Array<{
        id: string;
        question_id: string;
        answer_text: string | null;
        answer_option_ids: string[];
        has_audio: boolean;
        audio_duration_seconds: number | null;
        word_count: number | null;
      }>;
    }>(`/v1/attempts/${attemptId}/responses`),

  // Fetch the candidate's own audio recording as a Blob. The HTML
  // `<audio>` element can't send a Bearer header, so we fetch with
  // auth, then create an object URL the player can pipe into `src`.
  // Caller is responsible for revoking the returned URL when the
  // playback widget is destroyed.
  ownAudioBlob: async (
    attemptId: string,
    questionId: string,
  ): Promise<{ url: string; bytes: number } | null> => {
    // authedFetch, like its POST sibling above. This is the REHYDRATION path:
    // a candidate returning to a speaking question is shown the take they
    // already made. Its caller swallows errors and falls back to "idle", so an
    // expired token here silently offered a blank Start button and invited the
    // candidate to record over an answer already safely stored.
    const res = await authedFetch(
      `/v1/attempts/${attemptId}/responses/${questionId}/audio`,
    );
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new ApiError(
        res.status,
        "audio_fetch_failed",
        `audio fetch failed: HTTP ${res.status}`,
      );
    }
    const blob = await res.blob();
    return { url: URL.createObjectURL(blob), bytes: blob.size };
  },

  saveResponse: (
    attemptId: string,
    questionId: string,
    body: { answer_text?: string | null; answer_option_ids?: string[] },
  ) =>
    request<{ ok: true }>(
      `/v1/attempts/${attemptId}/responses/${questionId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  submitAttempt: (attemptId: string) =>
    request<SubmitResult | WritingSubmitResult>(
      `/v1/attempts/${attemptId}/submit`,
      { method: "POST" },
    ),

  // Fire-and-forget exam-integrity incident. Sends { kind, detail? } and
  // returns 202 on success. Failure is swallowed by the caller.
  recordAttemptIncident: (
    attemptId: string,
    body: { kind: string; detail?: string },
  ) =>
    request<null>(`/v1/attempts/${attemptId}/incidents`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getResult: (attemptId: string) =>
    request<AttemptResult | AttemptResultHeld>(
      `/v1/attempts/${attemptId}/result`,
    ),

  grammarCheck: (attemptId: string, questionId: string) =>
    request<GrammarCheckResult>(
      `/v1/attempts/${attemptId}/responses/${questionId}/grammar-check`,
      { method: "POST" },
    ),

  markingQueue: (
    kind: "all" | "first" | "second" | "moderator" = "all",
    opts: { mineOnly?: boolean } = {},
  ) => {
    const params = new URLSearchParams({ kind });
    if (opts.mineOnly) params.set("mine_only", "1");
    return request<{
      queue: Array<
        MarkingQueueItem & {
          kind: "first" | "second" | "moderator";
          assigned_marker_user_id: string | null;
          // Resolved assignee identity; null when unassigned or the user
          // was deleted (fall back to the short id).
          marker_name: string | null;
          marker_email: string | null;
          days_waiting: number;
          is_overdue: boolean;
        }
      >;
    }>(`/v1/marking/queue?${params.toString()}`);
  },

  // Active examiners of the org, for the assign-marker picker. Scoped to the
  // marking surface so moderators (below the tenant_admin gate on
  // /v1/org/members) can still populate it.
  listMarkingExaminers: () =>
    request<{
      examiners: Array<{ user_id: string; full_name: string; email: string }>;
    }>(`/v1/marking/examiners`),

  assignMarker: (
    attemptId: string,
    kind: "first" | "second" | "moderator",
    markerUserId: string,
  ) =>
    request<{ session_id: string; kind: string; marker_user_id: string }>(
      `/v1/marking/attempts/${attemptId}/assign-marker`,
      {
        method: "POST",
        body: JSON.stringify({ kind, marker_user_id: markerUserId }),
      },
    ),

  markingAttempt: (attemptId: string) =>
    request<
      MarkingAttemptDetail & {
        session: { id: string; kind: "first" | "second" | "moderator"; status: string };
      }
    >(`/v1/marking/attempts/${attemptId}`),

  // Examiner-only session-recording manifest. Returns the reassembled rrweb
  // event stream + session/action metadata, or `{ available:false }` when the
  // attempt was never recorded. Loaded lazily by SessionReplayPanel on demand.
  attemptRecordingManifest: (attemptId: string) =>
    request<RecordingManifest>(`/v1/marking/attempts/${attemptId}/recording`),

  scoreResponse: (
    responseId: string,
    sessionId: string,
    body: {
      scores: Array<{ criterion_id: string; score: number; comment?: string }>;
      feedback?: string;
    },
  ) =>
    request<{ ok: true }>(
      `/v1/marking/responses/${responseId}/score?session_id=${encodeURIComponent(sessionId)}`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  finaliseMarking: (attemptId: string, sessionId: string) =>
    request<{
      attempt_id: string;
      status: string;
      score_raw: number;
      score_max: number;
      score_percent: number;
      passed: boolean;
      agreement?: AgreementSummary;
    }>(
      `/v1/marking/attempts/${attemptId}/finalise?session_id=${encodeURIComponent(sessionId)}`,
      { method: "POST" },
    ),

  requestSecondMarking: (attemptId: string) =>
    request<{ ok: true }>(
      `/v1/marking/attempts/${attemptId}/request-second-mark`,
      { method: "POST" },
    ),

  moderationDetail: (attemptId: string) =>
    request<ModerationDetail>(
      `/v1/marking/attempts/${attemptId}/moderation`,
    ),

  resolveModeration: (
    attemptId: string,
    body: {
      resolutions: Array<{
        response_id: string;
        choice: "first" | "second" | "average" | "override";
        override_marks?: number;
      }>;
    },
  ) =>
    request<{
      attempt_status: "reviewed";
      score_percent: number;
      passed: boolean;
    }>(`/v1/marking/attempts/${attemptId}/resolve-moderation`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  uploadAudio: async (
    attemptId: string,
    questionId: string,
    blob: Blob,
    durationSeconds: number,
  ) => {
    // authedFetch, not fetch: an expired token here loses a candidate's spoken
    // answer mid-exam, and the retry button would re-send the same dead token.
    const res = await authedFetch(
      `/v1/attempts/${attemptId}/responses/${questionId}/audio`,
      {
        method: "POST",
        headers: {
          "content-type": blob.type || "audio/webm",
          "x-audio-duration-seconds": String(Math.round(durationSeconds)),
        },
        body: blob,
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        body?.error?.code ?? "internal",
        body?.error?.message ?? res.statusText,
        body?.error?.details,
      );
    }
    return (await res.json()) as {
      response_id: string;
      answer_r2_key: string;
      content_type: string;
      bytes: number;
      duration_seconds: number;
    };
  },

  fetchAudioBlob: async (responseId: string): Promise<Blob> => {
    const res = await authedFetch(
      `/v1/marking/responses/${responseId}/audio`,
    );
    if (!res.ok) {
      throw new ApiError(res.status, "internal", "failed to fetch audio");
    }
    return await res.blob();
  },

  rerunTranscription: (responseId: string) =>
    request<{ enqueued: boolean }>(
      `/v1/marking/responses/${responseId}/transcribe`,
      { method: "POST" },
    ),

  // Session recording (best-effort capture of the candidate's exam page). The
  // backend gates start on assessment.record_session + ownership + in_progress;
  // these client methods never throw into the recorder's own best-effort guards.
  // Returns the attempt's current chunk_count so a resumed recording (reload /
  // remount) can seed its next seq and APPEND, never overwriting R2 seq 0..n.
  startRecording: (
    attemptId: string,
    body: { session_meta: unknown; consent_text: string },
  ) =>
    request<{ ok: true; chunk_count: number }>(
      `/v1/attempts/${attemptId}/recording/start`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),

  // Binary chunk upload. Mirrors uploadAudio: a raw body (here a gzipped rrweb
  // event batch) POSTed with the auth headers and an explicit binary
  // content-type, NOT via the JSON `request` helper. seq is the chunk index.
  uploadRecordingChunk: async (
    attemptId: string,
    seq: number,
    body: Uint8Array,
  ) => {
    // authedFetch: without it every chunk after the token's 15th minute 401'd,
    // so invigilation replay for any longer exam ended silently part-way and
    // looked like a candidate who stopped interacting.
    const res = await authedFetch(
      `/v1/attempts/${attemptId}/recording/chunk?seq=${seq}`,
      {
        method: "POST",
        headers: { "content-type": "application/octet-stream" },
        body: body as BodyInit,
      },
    );
    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        errBody?.error?.code ?? "internal",
        errBody?.error?.message ?? res.statusText,
        errBody?.error?.details,
      );
    }
    return (await res.json().catch(() => ({}))) as { ok?: true };
  },

  finalizeRecording: (attemptId: string, body: { stats: unknown }) =>
    request<{ ok: true }>(`/v1/attempts/${attemptId}/recording/finalize`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getAssessment: (id: string) => request<AuthoringAssessment>(`/v1/assessments/${id}`),

  updateAssessment: (
    id: string,
    body: {
      title?: string;
      instructions?: string;
      duration_seconds?: number;
      pass_threshold_percent?: number;
      grading_mode?: "pass_fail" | "raw_score";
      max_attempts?: number;
      // Enterprise settings. Nullable fields accept null to clear.
      opens_at?: string | null;
      closes_at?: string | null;
      late_submission_grace_seconds?: number;
      shuffle_questions?: boolean;
      shuffle_options?: boolean;
      result_release_mode?: "immediate" | "manual_hold";
  /** Supersedes result_release_mode when set. See services/resultRelease. */
  result_release_policy?: ResultReleasePolicy | null;
  /** Target instant for the "scheduled" policy. */
  results_release_at?: string | null;
      results_released_at?: string | null;
      candidate_can_see_score?: boolean;
      candidate_can_see_per_question?: boolean;
      candidate_can_see_correct_answers?: boolean;
      candidate_can_see_feedback?: boolean;
      score_reporting?: "percent" | "band";
      band_boundaries?: Array<{ label: string; min_percent: number }> | null;
      draw_sections_count?: number | null;
      // Per-student question pool: draw N random questions per attempt.
      // Null clears the draw (show all).
      display_question_count?: number | null;
      skip_preflight?: boolean;
      // Per-assessment session-recording toggle (default OFF). Consent-gated
      // on-screen capture for invigilation review.
      record_session?: boolean;
      // When true the exam runner allows copy/paste/context-menu (no block,
      // no incident). Default false = strict exam conditions.
      allow_copy_paste?: boolean;
      // Mock (practice) paper: sat exactly like a real exam, but never
      // certifiable and excluded from every dashboard statistic.
      is_mock?: boolean;
      // "Assessment Configuration" validation (Assessor + Internal Verifier),
      // pre-serialized via serializeAssessmentValidation; null clears.
      validation_json?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/v1/assessments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteAssessment: (id: string) =>
    request<{ ok: true }>(`/v1/assessments/${id}`, { method: "DELETE" }),

  publishAssessment: (id: string) =>
    request<{ ok: true }>(`/v1/assessments/${id}/publish`, { method: "POST" }),

  // Archive / unarchive — staff-side organisational flag only. Archiving hides
  // the assessment from the default (non-archived) authoring list and locks the
  // editor read-only; it never touches sections/questions/attempts. Unarchive
  // drops it back to draft (never straight to published) so the paper is
  // re-exposed only by an explicit re-publish. Archive is refused (409) while a
  // live sitting still references the assessment.
  archiveAssessment: (id: string) =>
    request<{ ok: true }>(`/v1/assessments/${id}/archive`, { method: "POST" }),

  unarchiveAssessment: (id: string) =>
    request<{ ok: true }>(`/v1/assessments/${id}/unarchive`, { method: "POST" }),

  listAttemptOverrides: (assessmentId: string) =>
    request<{ overrides: AttemptOverrideView[] }>(
      `/v1/assessments/${assessmentId}/overrides`,
    ),

  createAttemptOverride: (
    assessmentId: string,
    body: {
      candidate_email: string;
      extra_time_seconds?: number;
      extra_attempts?: number;
      opens_at?: string | null;
      closes_at?: string | null;
      note?: string;
    },
  ) =>
    request<{ id: string; user_id: string }>(
      `/v1/assessments/${assessmentId}/overrides`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  updateAttemptOverride: (
    overrideId: string,
    body: {
      extra_time_seconds?: number;
      extra_attempts?: number;
      opens_at?: string | null;
      closes_at?: string | null;
      note?: string;
    },
  ) =>
    request<{ ok: true }>(`/v1/attempt-overrides/${overrideId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteAttemptOverride: (overrideId: string) =>
    request<{ ok: true }>(`/v1/attempt-overrides/${overrideId}`, {
      method: "DELETE",
    }),

  // ── Sittings ─────────────────────────────────────────────────
  listSittings: (
    filters: {
      status?: string;
      level_code?: string;
      programme?: "fs_english" | "heda";
      limit?: number;
      cursor?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.level_code) params.set("level_code", filters.level_code);
    if (filters.programme) params.set("programme", filters.programme);
    if (filters.limit != null) params.set("limit", String(filters.limit));
    if (filters.cursor) params.set("cursor", filters.cursor);
    const qs = params.toString();
    return request<{ sittings: SittingRow[]; next_cursor: string | null }>(
      `/v1/sittings${qs ? `?${qs}` : ""}`,
    );
  },

  getSitting: (id: string) => request<SittingDetail>(`/v1/sittings/${id}`),

  createSitting: (body: {
    title: string;
    level_code: string;
    assessment_ids: string[];
    opens_at?: string | null;
    closes_at?: string | null;
    session_cap_seconds?: number | null;
    order_mode?: "free" | "linear";
  }) =>
    request<{ id: string }>(`/v1/sittings`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateSitting: (
    id: string,
    body: {
      title?: string;
      assessment_ids?: string[];
      opens_at?: string | null;
      closes_at?: string | null;
      session_cap_seconds?: number | null;
      order_mode?: "free" | "linear";
      // "Sitting Configuration" validation (Exams Officer + Lead Invigilator),
      // pre-serialized via serializeSittingValidation; null clears.
      validation_json?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/v1/sittings/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteSitting: (id: string) =>
    request<{ ok: true }>(`/v1/sittings/${id}`, { method: "DELETE" }),

  publishSitting: (id: string) =>
    request<{ ok: true }>(`/v1/sittings/${id}/publish`, { method: "POST" }),

  // Archive / unarchive — staff-side organisational flag only. Archiving hides
  // the sitting from the default (non-archived) staff list and locks the editor
  // read-only; it never touches candidate-facing state. Unarchive drops it back
  // to draft (never straight to published) so candidate access is re-opened
  // only by an explicit re-publish.
  archiveSitting: (id: string) =>
    request<{ ok: true }>(`/v1/sittings/${id}/archive`, { method: "POST" }),

  unarchiveSitting: (id: string) =>
    request<{ ok: true }>(`/v1/sittings/${id}/unarchive`, { method: "POST" }),

  // Duplicate — clone a sitting into a pre-filled DRAFT (copies level, the
  // assessment line-up, order_mode, session cap and validation signatories; the
  // schedule window is cleared so the copy can't go live by accident). Returns
  // the new draft's id so the caller can navigate to its editor.
  duplicateSitting: (id: string) =>
    request<{ id: string }>(`/v1/sittings/${id}/duplicate`, { method: "POST" }),

  getSittingHub: (id: string) =>
    request<SittingHub>(`/v1/sittings/${id}/hub`),

  // Teacher "preview as student" hub — same shape, synthesized (all slots
  // not_started, no real runs). Pairs with startAssessmentPreview per slot.
  getSittingHubPreview: (id: string) =>
    request<SittingHub>(`/v1/sittings/${id}/preview-hub`),

  enterSittingSlot: (sittingId: string, slotId: string) =>
    request<{
      attempt_id: string;
      expires_at: string;
      duration_seconds: number;
      assessment: StudentAssessment;
    }>(`/v1/sittings/${sittingId}/slots/${slotId}/enter`, { method: "POST" }),

  leaveSittingSlot: (sittingId: string, slotId: string) =>
    request<{ elapsed_seconds: number; time_remaining_seconds: number }>(
      `/v1/sittings/${sittingId}/slots/${slotId}/leave`,
      { method: "POST" },
    ),

  getSittingResult: (id: string) =>
    request<SittingResult>(`/v1/sittings/${id}/result`),

  // Staff completion overview — the AGGREGATE across every candidate (N of M
  // submitted, per-slot submitted/scored/passed, per-candidate breakdown).
  // Staff-only (requireMinRole teacher; examiners qualify); read-only.
  getSittingOverview: (id: string) =>
    request<SittingResultsOverview>(`/v1/sittings/${id}/overview`),

  // Staff LIVE-MONITOR — the read-only invigilation view: per candidate + slot,
  // the live status (not_started/in_progress/paused/submitted), time remaining
  // (in_progress tick folded in), and a paused flag. Staff-only (requireMinRole
  // teacher; examiners qualify); read-only — never mutates candidate run state.
  getSittingLiveMonitor: (id: string) =>
    request<SittingLiveMonitor>(`/v1/sittings/${id}/monitor`),

  issueSittingCertificate: (id: string) =>
    request<{
      cert_no: string;
      issued_at: string;
      valid_until: string;
      // Canonical candidate name resolved server-side from the users
      // table — the authoritative value to print on the PDF, even after
      // an admin name correction the local JWT hasn't caught up to.
      candidate_name: string;
      // Whether a durable candidate photo was copied onto the cert (Phase 3);
      // when true the client fetches it (fetchCertificatePhotoBlob) for the PDF.
      has_candidate_photo: boolean;
      // Award wording stamped at issue (from the programme template). Printed on
      // the PDF and shown on /verify. Null when the programme set no award_label.
      award_line: string | null;
      // Stable public code for the issuing institution — the cert's ID block.
      institution_id: string;
      // True for a test entry-slip candidate — the PDF gets a SPECIMEN stamp
      // and /verify marks it not a valid award.
      is_test: boolean;
    }>(`/v1/sittings/${id}/certificate`, { method: "POST" }),

  issueAttemptCertificate: (attemptId: string) =>
    request<{
      cert_no: string;
      issued_at: string;
      valid_until: string;
      candidate_name: string;
      has_candidate_photo: boolean;
      award_line: string | null;
      institution_id: string;
      // Assessment display fields — so a revisit download (bootstrap gone)
      // still renders the real title/level/module, not the "Assessment" default.
      title: string;
      module: string;
      level_code: string;
      duration_seconds: number;
      // SPECIMEN stamp for a test entry-slip candidate.
      is_test: boolean;
    }>(`/v1/attempts/${attemptId}/certificate`, { method: "POST" }),

  deleteSection: (id: string) =>
    request<{ ok: true }>(`/v1/sections/${id}`, { method: "DELETE" }),

  deleteQuestion: (id: string) =>
    request<{ ok: true }>(`/v1/questions/${id}`, { method: "DELETE" }),

  // In-place section / question / option / answer edits. Match the
  // server's PATCH payloads; every field optional.
  updateSection: (
    id: string,
    body: {
      title?: string;
      instructions?: string;
      stimulus_text?: string;
      /** Accessibility alt text for the section stimulus
       *  (image / audio). Max 500 chars. */
      alt_text?: string;
      time_limit_seconds?: number | null;
      max_replays?: number;
      order_index?: number;
    },
  ) =>
    request<{ ok: true }>(`/v1/sections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  reorderSection: (id: string, direction: "up" | "down") =>
    request<{ ok: true }>(`/v1/sections/${id}/reorder`, {
      method: "POST",
      body: JSON.stringify({ direction }),
    }),

  updateQuestion: (
    id: string,
    body: {
      prompt?: string;
      marks?: number;
      metadata?: Record<string, unknown>;
      order_index?: number;
    },
  ) =>
    request<{ ok: true }>(`/v1/questions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  reorderQuestion: (id: string, direction: "up" | "down") =>
    request<{ ok: true }>(`/v1/questions/${id}/reorder`, {
      method: "POST",
      body: JSON.stringify({ direction }),
    }),

  // Deep-copy a question (options/answers + per-option metadata) into the same
  // section, just after the original. Returns the new question id.
  duplicateQuestion: (id: string) =>
    request<{ id: string }>(`/v1/questions/${id}/duplicate`, {
      method: "POST",
    }),

  // Re-parent a question to another section of the same assessment.
  moveQuestion: (id: string, sectionId: string) =>
    request<{ ok: true }>(`/v1/questions/${id}/move`, {
      method: "POST",
      body: JSON.stringify({ section_id: sectionId }),
    }),

  addQuestionOption: (
    questionId: string,
    body: { text: string; is_correct: boolean; order_index?: number },
  ) =>
    request<{ id: string }>(`/v1/questions/${questionId}/options`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateQuestionOption: (
    id: string,
    body: { text?: string; is_correct?: boolean; order_index?: number },
  ) =>
    request<{ ok: true }>(`/v1/question-options/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteQuestionOption: (id: string) =>
    request<{ ok: true }>(`/v1/question-options/${id}`, { method: "DELETE" }),

  addQuestionAnswer: (
    questionId: string,
    body: {
      accepted_answer: string;
      match_type: "exact" | "case_insensitive" | "regex" | "fuzzy";
      marks: number;
    },
  ) =>
    request<{ id: string }>(`/v1/questions/${questionId}/answers`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateQuestionAnswer: (
    id: string,
    body: {
      accepted_answer?: string;
      match_type?: "exact" | "case_insensitive" | "regex" | "fuzzy";
      marks?: number;
    },
  ) =>
    request<{ ok: true }>(`/v1/question-answers/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteQuestionAnswer: (id: string) =>
    request<{ ok: true }>(`/v1/question-answers/${id}`, { method: "DELETE" }),

  // Reading
  createReadingAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/assessments", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createReadingSection: (
    assessmentId: string,
    body: CreateReadingSectionBody,
  ) =>
    request<{ id: string }>(`/v1/assessments/${assessmentId}/sections`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createReadingQuestion: (sectionId: string, body: CreateReadingQuestionBody) =>
    request<{ id: string }>(`/v1/sections/${sectionId}/questions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Writing
  createWritingAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/writing/assessments", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createWritingPrompt: (assessmentId: string, body: CreateWritingPromptBody) =>
    request<{ section_id: string; question_id: string }>(
      `/v1/writing/assessments/${assessmentId}/prompts`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  createRubric: (assessmentId: string, body: CreateRubricBody) =>
    request<{ rubric_id: string; criteria: Array<{ id: string; name: string }> }>(
      `/v1/writing/assessments/${assessmentId}/rubrics`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  updateRubric: (rubricId: string, body: CreateRubricBody) =>
    request<{ rubric_id: string; criteria: Array<{ id: string; name: string }> }>(
      `/v1/writing/rubrics/${rubricId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  deleteRubric: (rubricId: string) =>
    request<{ ok: true }>(`/v1/writing/rubrics/${rubricId}`, {
      method: "DELETE",
    }),

  getWritingRubric: (assessmentId: string) =>
    request<{
      rubric: {
        id: string;
        name: string;
        scale_max: number;
        criteria: Array<{
          id: string;
          name: string;
          description: string;
          weight: number;
        }>;
      } | null;
    }>(`/v1/writing/assessments/${assessmentId}/rubric`),

  // Speaking
  createSpeakingAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/speaking/assessments", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createSpeakingPrompt: (
    assessmentId: string,
    body: CreateSpeakingPromptBody,
  ) =>
    request<{ section_id: string; question_id: string }>(
      `/v1/speaking/assessments/${assessmentId}/prompts`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // Listening
  createListeningAssessment: (body: CreateAssessmentBody) =>
    request<{ id: string }>("/v1/listening/assessments", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createListeningSection: (
    assessmentId: string,
    body: { order_index: number; title: string; instructions?: string; max_replays?: number },
  ) =>
    request<{ id: string }>(
      `/v1/listening/assessments/${assessmentId}/sections`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  uploadSectionAudio: async (
    sectionId: string,
    blob: Blob,
    durationSeconds: number,
  ) => {
    const res = await authedFetch(
      `/v1/listening/sections/${sectionId}/audio`,
      {
        method: "POST",
        headers: {
          "content-type": blob.type || "audio/webm",
          "x-audio-duration-seconds": String(Math.round(durationSeconds)),
        },
        body: blob,
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        body?.error?.code ?? "internal",
        body?.error?.message ?? res.statusText,
      );
    }
    return (await res.json()) as {
      section_id: string;
      stimulus_r2_key: string;
      content_type: string;
      bytes: number;
      duration_seconds: number;
    };
  },

  listPlatformTenants: (opts?: {
    status?: string;
    q?: string;
    cursor?: string | null;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.q) qs.set("q", opts.q);
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const q = qs.toString() ? `?${qs}` : "";
    return request<{
      tenants: PlatformTenantSummary[];
      next_cursor: string | null;
      total: number;
    }>(`/v1/platform/organisations${q}`);
  },

  // Admin-initiated tenant provisioning (Phase 7a). Creates the org + founding
  // tenant_admin (is_founder=1) and returns a setup token the founder redeems
  // at /reset-password/:token to set their own password.
  createTenant: (body: {
    org_name: string;
    org_slug: string;
    founder_email: string;
    founder_name: string;
  }) =>
    request<{
      organisation_id: string;
      organisation_slug: string;
      founder_user_id: string;
      setup_token: string;
      setup_expires_at: string;
    }>("/v1/platform/organisations", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getPlatformTenant: (id: string) =>
    request<PlatformTenantDetail>(`/v1/platform/organisations/${id}`),

  // Keyset-paginated full membership list (the detail payload only carries
  // the recent-20 window). `cursor` opaque; null/omitted = first page.
  listTenantMembers: (id: string, cursor?: string | null) => {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return request<{
      members: Array<{
        id: string;
        role: string;
        status: string;
        joined_at: string;
        user_id: string;
        is_founder: number;
        user_email: string | null;
        user_full_name: string | null;
        email_verified_at: string | null;
      }>;
      next_cursor: string | null;
    }>(`/v1/platform/organisations/${id}/members${qs}`);
  },

  // Support override: stamp a member's email verified after out-of-band
  // identity confirmation (verification email lost to spam/typo). Audited;
  // lifts the login gate immediately.
  verifyTenantMemberEmail: (orgId: string, userId: string) =>
    request<{ ok: true; unchanged?: boolean }>(
      `/v1/platform/organisations/${orgId}/members/${userId}/verify-email`,
      { method: "POST" },
    ),

  // Authed fetch → blob → anchor for a single nightly backup JSON snapshot.
  // Mirrors the OrgAudit downloadCsv auth+blob+anchor pattern. `date` is the
  // YYYY-MM-DD parsed from the backup key.
  downloadTenantBackup: async (id: string, date: string) => {
    const headers: Record<string, string> = {};
    const res = await fetch(
      `${API_BASE_URL}/v1/platform/organisations/${id}/backups/${date}`,
      { headers, credentials: "include" },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        "backup_download_failed",
        res.status === 404
          ? "backup not found"
          : `download failed: ${res.status}`,
      );
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${id}_${date}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  suspendTenant: (id: string) =>
    request<{ ok: true }>(`/v1/platform/organisations/${id}/suspend`, {
      method: "POST",
    }),

  unsuspendTenant: (id: string) =>
    request<{ ok: true }>(`/v1/platform/organisations/${id}/unsuspend`, {
      method: "POST",
    }),

  listPlatformBackups: (id: string) =>
    request<{
      organisation_id: string;
      slug: string;
      backups: Array<{ key: string; size: number; uploaded: string }>;
    }>(`/v1/platform/organisations/${id}/backups`),
  reprovisionTenant: (id: string) =>
    request<{ enqueued: boolean }>(
      `/v1/platform/organisations/${id}/reprovision`,
      { method: "POST" },
    ),
  migrateTenantSchema: (id: string) =>
    request<{ schema_version: number }>(
      `/v1/platform/organisations/${id}/migrate-schema`,
      { method: "POST" },
    ),
  // Emergency: set a fresh temporary password for a locked-out tenant admin.
  // Returns the password once for the operator to relay out of band.
  resetTenantAdminPassword: (orgId: string, userId: string, reason: string) =>
    request<{ ok: true; user_email: string | null; temporary_password: string }>(
      `/v1/platform/organisations/${orgId}/reset-admin-password`,
      { method: "POST", body: JSON.stringify({ user_id: userId, reason }) },
    ),

  // Manually enqueue the per-org metered-billing reconcile (Phase C2). Mirrors
  // POST /v1/platform/organisations/:id/reconcile-billing — super_admin only.
  reconcileTenantBilling: (id: string) =>
    request<{ ok: true; enqueued: boolean }>(
      `/v1/platform/organisations/${id}/reconcile-billing`,
      { method: "POST" },
    ),

  platformMetrics: () => request<PlatformMetrics>("/v1/platform/metrics"),

  // ---- Platform: error inbox ---------------------------------------------
  // Grouped application errors. One row per distinct fault with a running
  // count, so an outage reads as one entry, not a flood.
  listPlatformErrors: (filter: "open" | "all" = "open") =>
    request<{ errors: PlatformError[]; open_count: number }>(
      `/v1/platform/errors?filter=${filter}`,
    ),
  resolvePlatformError: (fingerprint: string) =>
    request<PlatformError>(
      `/v1/platform/errors/${encodeURIComponent(fingerprint)}/resolve`,
      { method: "POST" },
    ),

  // ---- Platform: diagnostics + dead-letter queue (Phase 5) ----------------
  runDiagnostics: () =>
    request<{
      generated_at: string;
      checks: Array<{ name: string; ok: boolean; detail: string }>;
    }>("/v1/platform/diagnostics"),
  listQueuePoison: (cursor?: string | null) =>
    request<{
      messages: Array<{
        key: string;
        kind: string;
        ts: number;
        uploaded: string;
        size: number;
      }>;
      cursor: string | null;
    }>(
      `/v1/platform/queue-poison${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  getQueuePoisonMessage: (key: string) =>
    request<{
      enqueued_at: string;
      kind: string;
      attempts: number;
      error: string;
      parent_request_id: string | null;
      body: unknown;
    }>(`/v1/platform/queue-poison/message?key=${encodeURIComponent(key)}`),
  replayQueuePoison: (key: string) =>
    request<{ ok: true; kind: string }>("/v1/platform/queue-poison/replay", {
      method: "POST",
      body: JSON.stringify({ key }),
    }),

  // ---- Platform: audit chain integrity (Phase 1) --------------------------
  verifyPlatformAuditChain: () =>
    request<{
      ok: boolean;
      checked: number;
      skipped_unhashed: number;
      broken_at?: {
        id: string;
        created_at: string;
        expected: string | null;
        actual: string | null;
      };
    }>("/v1/platform/audit/verify"),
  backfillPlatformAuditChain: () =>
    request<{ signed: number; scanned: number }>(
      "/v1/platform/audit/backfill?confirm=true",
      { method: "POST" },
    ),

  // ---- Platform: parked billing usage (failed Stripe meter events) --------
  listParkedUsage: (limit?: number) =>
    request<{
      parked: Array<{
        organisation_id: string;
        event_key: string;
        billing_period: string;
        module: string | null;
        usage_at: string | null;
        report_failed_at: string;
        report_error_code: string | null;
        report_error_message: string | null;
        voided_at: string | null;
      }>;
      count: number;
    }>(`/v1/platform/billing/parked-usage${limit ? `?limit=${limit}` : ""}`),
  // Re-attempt the Stripe meter report for a parked row. `result` mirrors the
  // reporter's {reported, reason}; on success the row leaves the parked set.
  retryParkedUsage: (organisation_id: string, event_key: string) =>
    request<{ ok: true; result: { reported: boolean; reason?: string } }>(
      "/v1/platform/billing/parked-usage/retry",
      {
        method: "POST",
        body: JSON.stringify({ organisation_id, event_key }),
      },
    ),
  // Write off a parked row (never deletes it — sets voided_at, excluding it
  // from the parked set). A reason is recorded in the platform audit.
  voidParkedUsage: (organisation_id: string, event_key: string, reason: string) =>
    request<{ ok: true }>("/v1/platform/billing/parked-usage/void", {
      method: "POST",
      body: JSON.stringify({ organisation_id, event_key, reason }),
    }),

  // ---- Platform: billing reconciliation (ledger vs reported/parked) -------
  // Per-org/per-period rollup of the billing_usage_events ledger so an operator
  // can see whether every metered unit reported to Stripe. drift = parked > 0
  // (the dropped-revenue signal). Read-only; the period defaults to the current
  // UTC YYYY-MM server-side when omitted.
  getBillingReconciliation: (period?: string) =>
    request<{
      period: string;
      rows: Array<{
        organisation_id: string;
        slug: string | null;
        name: string;
        events: number;
        ledger_qty: number;
        reported: number;
        parked: number;
        voided: number;
        drift: boolean;
      }>;
    }>(
      `/v1/platform/billing/reconciliation${period ? `?period=${encodeURIComponent(period)}` : ""}`,
    ),

  // ---- Platform: Stripe webhook event inspect + replay (Phase A2) ---------
  getBillingEvent: (stripeEventId: string) =>
    request<{
      id: string;
      stripe_event_id: string;
      type: string;
      organisation_id: string | null;
      processed_at: string | null;
      created_at: string;
      payload: unknown;
    }>(`/v1/platform/billing/events/${encodeURIComponent(stripeEventId)}`),
  replayBillingEvent: (stripeEventId: string) =>
    request<{ ok: true; handled: boolean }>(
      `/v1/platform/billing/events/${encodeURIComponent(stripeEventId)}/replay`,
      { method: "POST" },
    ),

  // ---- Platform: per-tenant retention policy ------------------------------
  setTenantRetention: (id: string, days: number | null) =>
    request<{ ok: true; retention_days: number | null }>(
      `/v1/platform/organisations/${id}/retention`,
      { method: "POST", body: JSON.stringify({ days }) },
    ),

  // ---- Platform: founder transfer -----------------------------------------
  transferTenantFounder: (id: string, userId: string) =>
    request<{
      ok: true;
      previous_founder_user_id: string | null;
      new_founder_user_id: string;
    }>(`/v1/platform/organisations/${id}/founder`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    }),

  getOrgUsage: () => request<OrgUsage>("/v1/org/usage"),

  // ---- Platform access (impersonation consent) ----------------------------
  getImpersonationPolicy: () =>
    request<{
      policy: "always_allow" | "case_by_case" | "always_deny";
      emergency_override_allowed: boolean;
      updated_by_user_id: string | null;
      updated_at: string;
    }>(`/v1/org/impersonation-policy`),
  setImpersonationPolicy: (body: {
    policy: "always_allow" | "case_by_case" | "always_deny";
    emergency_override_allowed: boolean;
  }) =>
    request<{
      policy: "always_allow" | "case_by_case" | "always_deny";
      emergency_override_allowed: boolean;
      updated_by_user_id: string | null;
      updated_at: string;
    }>(`/v1/org/impersonation-policy`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  listImpersonationGrants: () =>
    request<{
      grants: Array<{
        id: string;
        organisation_id: string;
        granted_by_user_id: string;
        granted_at: string;
        expires_at: string;
        scope_reason: string;
        revoked_at: string | null;
        revoked_by_user_id: string | null;
        consumed_session_id: string | null;
      }>;
    }>(`/v1/org/impersonation-grants`),
  createImpersonationGrant: (body: {
    expires_in_seconds: number;
    scope_reason: string;
  }) =>
    request<{
      grant: {
        id: string;
        expires_at: string;
        scope_reason: string;
        granted_at: string;
      };
    }>(`/v1/org/impersonation-grants`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeImpersonationGrant: (id: string) =>
    request<{ grant: { id: string; revoked_at: string | null } }>(
      `/v1/org/impersonation-grants/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),
  listTenantImpersonations: () =>
    request<{
      sessions: Array<{
        id: string;
        actor_user_id: string;
        actor_email: string | null;
        actor_full_name: string | null;
        reason: string | null;
        reason_category: string | null;
        view_as_role: string | null;
        view_as_readonly: number | null;
        started_at: string;
        ended_at: string | null;
        expires_at: string | null;
      }>;
    }>(`/v1/org/impersonations`),

  getBilling: () =>
    request<{
      plan: string;
      stripe_customer_id: string | null;
      subscription: {
        id: string;
        stripe_subscription_id: string;
        stripe_price_id: string;
        plan_code: "starter" | "growth" | "enterprise";
        status: string;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
      } | null;
      configured: boolean;
      purchasable_plans: Array<"starter" | "growth" | "enterprise">;
      // Per-tenant spend-cap config + the resolved effective cap (metered
      // billing P3/P4). The override is what the admin set; null ⇒ falling back
      // to the plan default. Mirrors apps/api/src/routes/org.ts GET /billing.
      hard_spend_cap_pence: number | null;
      plan_default_hard_spend_cap_pence: number | null;
      effective_hard_spend_cap_pence: number | null;
      // Highest spend-cap alert threshold (50/75/90) crossed this billing
      // period, or null when none crossed (or the org is uncapped/unmetered).
      // Drives the amber SpendAlertBanner. Mirrors GET /v1/org/billing.
      spend_alert_threshold: number | null;
      // The metered overage/cap summary. null for grandfathered tenants.
      metered: MeteredUsageSummary | null;
    }>("/v1/org/billing"),

  startCheckout: (plan: "starter" | "growth" | "enterprise") =>
    request<{ url: string }>("/v1/org/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),

  // Start metered billing. Omit `plan` for a PAYG checkout (£0 base + overage);
  // supply it for a subscription-mode metered checkout (plan base + overage).
  // Mirrors apps/api/src/routes/org.ts POST /billing/checkout/metered.
  startMeteredCheckout: (plan?: "starter" | "growth" | "enterprise") =>
    request<{ url: string }>("/v1/org/billing/checkout/metered", {
      method: "POST",
      body: JSON.stringify(plan ? { plan } : {}),
    }),

  // Set the per-tenant hard spend cap, in pence. null ⇒ clear the override
  // (fall back to the plan default); 0 ⇒ block all overage. Mirrors
  // apps/api/src/routes/org.ts PUT /billing/spend-cap.
  setSpendCap: (hardSpendCapPence: number | null) =>
    request<{
      hard_spend_cap_pence: number | null;
      plan_default_hard_spend_cap_pence: number | null;
      effective_hard_spend_cap_pence: number | null;
    }>("/v1/org/billing/spend-cap", {
      method: "PUT",
      body: JSON.stringify({ hard_spend_cap_pence: hardSpendCapPence }),
    }),

  openBillingPortal: () =>
    request<{ url: string }>("/v1/org/billing/portal", { method: "POST" }),

  listInvoices: () =>
    request<{
      invoices: Array<{
        id: string;
        number: string | null;
        status: string;
        amount_due: number;
        amount_paid: number;
        // Pre-tax subtotal, computed tax, and post-tax total — smallest
        // currency unit (pence for GBP). amount + tax = total always holds
        // (the API derives subtotal = total − tax when Stripe omits it).
        subtotal: number;
        tax: number;
        total: number;
        currency: string;
        // ISO-8601 timestamp string (the API maps Stripe's epoch-seconds to
        // an ISO string before returning).
        created: string;
        hosted_invoice_url: string | null;
        invoice_pdf: string | null;
      }>;
    }>("/v1/org/billing/invoices"),

  setTenantPlan: (id: string, plan: "starter" | "growth" | "enterprise") =>
    request<{ ok: true; plan?: string; unchanged?: boolean }>(
      `/v1/platform/organisations/${id}/plan`,
      { method: "POST", body: JSON.stringify({ plan }) },
    ),

  setPlanOverrides: (
    id: string,
    overrides: {
      max_published_assessments?: number;
      max_attempts_per_month?: number;
    } | null,
  ) =>
    request<{
      ok: true;
      overrides: {
        max_published_assessments?: number;
        max_attempts_per_month?: number;
      } | null;
    }>(`/v1/platform/organisations/${id}/plan-overrides`, {
      method: "POST",
      body: JSON.stringify({ overrides }),
    }),

  listPlatformQuestions: (opts?: {
    status?: string;
    module?: string;
    level_code?: string;
    type?: string;
    q?: string;
    tag?: string;
    programme_id?: string;
    quality_bucket?: string;
    issue?: string;
    // Slice the bank by review workflow stage:
    //   "pending"       → drafts submitted for 4-eyes review
    //   "not_submitted" → drafts not yet submitted
    review_state?: "pending" | "not_submitted";
    // Slice by moderation state:
    //   "flagged" → at least one OPEN moderation flag
    //   "clean"   → no open flags
    flag_state?: "flagged" | "clean";
    // Stimulus-set membership (migration 0040). A concrete `pqs_…` id →
    // that set's members; "none" → standalone questions (set_id IS NULL).
    set_id?: string;
    sort?: "recency" | "updated" | "marks" | "adoption" | "relevance" | "quality";
    direction?: "asc" | "desc";
    cursor?: string | null;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts ?? {})) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    const query = qs.toString() ? `?${qs}` : "";
    return request<{
      questions: PlatformQuestionSummary[];
      next_cursor: string | null;
      // Exact count of rows matching the filter (not the page size).
      // null on "load more" pages (only sent on the first page).
      total_matching?: number | null;
      // Module/level/bucket breakdown of the filtered set (first page
      // only). null → use global stats.
      facets?: {
        by_module: Record<string, number>;
        by_level: Record<string, number>;
        by_quality_bucket: Record<string, number>;
      } | null;
    }>(`/v1/platform/questions${query}`);
  },

  // --- Question bank governance (wave 2) ---
  listReviewQueue: (params?: {
    sort?: "oldest" | "newest" | "quality_asc" | "quality_desc" | "module";
    scope?: "all" | "mine" | "unclaimed";
    cursor?: string | null;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.sort) qs.set("sort", params.sort);
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.cursor) qs.set("cursor", params.cursor);
    qs.set("limit", String(params?.limit ?? 500));
    return request<{
      items: Array<
        PlatformQuestionSummary & {
          prompt_preview: string;
          submitted_for_review_at: string;
          submitted_by_user_id: string;
          submitted_by_email: string | null;
          review_notes: string | null;
          claimed_by_user_id: string | null;
        }
      >;
      next_cursor: string | null;
    }>(`/v1/platform/questions/review/queue?${qs.toString()}`);
  },

  reviewQueueCounts: () =>
    request<{
      total: number;
      stale: number;
      mine: number;
      by_bucket: Record<string, number>;
      by_module: Record<string, number>;
    }>(`/v1/platform/questions/review/queue/counts`),

  reviewBulkApprove: (ids: string[]) =>
    request<{
      requested: number;
      succeeded: number;
      skipped: Array<{ id: string; reason: string }>;
    }>(`/v1/platform/questions/review/bulk-approve`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  claimReviewQuestion: (id: string) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}/claim`, {
      method: "POST",
      body: "{}",
    }),

  unclaimReviewQuestion: (id: string) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}/unclaim`, {
      method: "POST",
      body: "{}",
    }),

  submitForReview: (id: string, notes?: string) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}/submit-for-review`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),

  approvePlatformQuestion: (id: string) =>
    request<{ ok: true; snapshot_version: number | null }>(
      `/v1/platform/questions/${id}/approve`,
      { method: "POST" },
    ),

  requestChangesPlatformQuestion: (
    id: string,
    notes: string,
    reason_codes?: string[],
  ) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}/request-changes`, {
      method: "POST",
      body: JSON.stringify({ notes, reason_codes }),
    }),

  listFlagsQueue: (onlyOpen = true) =>
    request<{
      items: Array<{
        id: string;
        platform_question_id: string;
        flagged_by_user_id: string;
        flagged_by_email: string | null;
        flagged_at: string;
        category: string;
        reason: string;
        resolved_at: string | null;
        resolved_by_user_id: string | null;
        resolution: string | null;
        resolution_note: string | null;
        question_prompt: string | null;
        // Stimulus-set membership (migration 0040) for a "part of set" pill.
        set_id?: string | null;
        set_order?: number | null;
      }>;
    }>(`/v1/platform/questions/flags/queue?open=${onlyOpen ? "true" : "false"}`),

  flagPlatformQuestion: (
    id: string,
    body: {
      category:
        | "factual_error"
        | "offensive_content"
        | "bias_or_discrimination"
        | "accessibility"
        | "curriculum_drift"
        | "duplicate"
        | "other";
      reason: string;
    },
  ) =>
    request<{ flag: { id: string } }>(`/v1/platform/questions/${id}/flags`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listQuestionFlags: (id: string) =>
    request<{ items: QuestionFlag[] }>(
      `/v1/platform/questions/${id}/flags`,
    ),

  // AI assist — cleaned prompt + confident-only option/answer
  // suggestions. Pure suggestion; nothing is written server-side.
  aiAssistQuestion: (id: string) =>
    request<QuestionAssistResult>(
      `/v1/platform/questions/${id}/ai-assist`,
      { method: "POST" },
    ),

  resolveFlag: (
    flagId: string,
    body: { resolution: "upheld" | "dismissed" | "duplicate"; note?: string },
  ) =>
    request<{ ok: true }>(
      `/v1/platform/questions/flags/${flagId}/resolve`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  replacePlatformQuestionOptions: (
    id: string,
    options: Array<{ order_index: number; text: string; is_correct: boolean }>,
  ) =>
    request<{ content_hash: string }>(`/v1/platform/questions/${id}/options`, {
      method: "PUT",
      body: JSON.stringify({ options }),
    }),

  // Attach (or detach with content_hash:null) a reading-passage image.
  // Upload the bytes first via uploadPlatformQuestionAsset → returns
  // content_hash, then point the question at it here.
  setPlatformQuestionStimulusImage: (
    id: string,
    content_hash: string | null,
    content_type?: string | null,
  ) =>
    request<{ stimulus_image_hash: string | null }>(
      `/v1/platform/questions/${id}/stimulus-image`,
      {
        method: "PUT",
        body: JSON.stringify({ content_hash, content_type: content_type ?? null }),
      },
    ),

  replacePlatformQuestionAnswers: (
    id: string,
    answers: Array<{
      accepted_answer: string;
      match_type: "exact" | "case_insensitive" | "regex" | "fuzzy";
      marks: number;
      tolerance_absolute?: number;
      tolerance_percent?: number;
      units?: string;
      significant_digits?: number;
    }>,
  ) =>
    request<{ content_hash: string }>(`/v1/platform/questions/${id}/answers`, {
      method: "PUT",
      body: JSON.stringify({ answers }),
    }),

  bulkUpdatePlatformQuestionMetadata: (body: {
    ids: string[];
    tags?: string[];
    level_code?: "E1" | "E2" | "E3" | "L1" | "L2" | "D1" | "D2" | "D3";
    programme_id?: string | null;
  }) =>
    request<{
      requested: number;
      updated: number;
      skipped: Array<{ id: string; reason: string }>;
    }>(`/v1/platform/questions/bulk/metadata`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getPlatformQuestionContentStandards: (id: string) =>
    request<{ codes: string[] }>(
      `/v1/platform/questions/${id}/content-standards`,
    ),

  replacePlatformQuestionContentStandards: (id: string, codes: string[]) =>
    request<{ ok: true; codes: string[] }>(
      `/v1/platform/questions/${id}/content-standards`,
      { method: "PUT", body: JSON.stringify({ codes }) },
    ),

  platformQuestionsContentStandardSummary: (ids: string[]) =>
    request<{
      summary: Array<{ code: string; marks: number; question_count: number }>;
    }>(`/v1/platform/questions/content-standards/summary`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  listPlatformQuestionVersions: (id: string) =>
    request<{
      versions: Array<{
        id: string;
        platform_question_id: string;
        version: number;
        snapshot_json: unknown;
        content_hash: string;
        published_at: string;
        published_by_user_id: string | null;
      }>;
    }>(`/v1/platform/questions/${id}/versions`),

  getPlatformQuestion: (id: string) =>
    request<PlatformQuestionDetail>(`/v1/platform/questions/${id}`),

  createPlatformQuestion: (body: CreatePlatformQuestionBody) =>
    request<{ id: string }>("/v1/platform/questions", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updatePlatformQuestion: (
    id: string,
    body: {
      prompt?: string;
      stimulus_text?: string;
      marks?: number;
      tags?: string[];
      programme_id?: string | null;
      alt_text?: Record<string, string>;
      transcript?: string;
      extra_time_allowed?: number;
      // 0030 bundle-shape fields exposed for editing.
      word_limit?: number | null;
      skill?: string | null;
      // Stimulus-set membership (migration 0040). Re-attach by passing a
      // new set_id; detach via set_id: null. set_order repositions.
      set_id?: string | null;
      set_order?: number | null;
    },
  ) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // Atomic full save — metadata + options + answers in ONE request.
  // Replaces the old 4-call editor sequence that could half-write a
  // question. Returns which steps ran + the refreshed detail.
  savePlatformQuestionFull: (
    id: string,
    body: {
      prompt?: string;
      stimulus_text?: string;
      marks?: number;
      tags?: string[];
      programme_id?: string | null;
      alt_text?: Record<string, string>;
      transcript?: string;
      extra_time_allowed?: number;
      content_standards?: string[];
      word_limit?: number | null;
      skill?: string | null;
      options?: Array<{
        order_index: number;
        text: string;
        is_correct: boolean;
        pair_group?: number;
        pair_side?: 0 | 1;
        // First-class option image (master 0041): a match_pairs item can be a
        // picture. Content hash from POST /v1/platform/question-assets; omit /
        // null for text-only options.
        image_hash?: string | null;
      }>;
      answers?: Array<{
        accepted_answer: string;
        match_type: "exact" | "case_insensitive" | "regex" | "fuzzy";
        marks: number;
        tolerance_absolute?: number;
        tolerance_percent?: number;
        units?: string;
        significant_digits?: number;
      }>;
    },
  ) =>
    request<{
      ok: boolean;
      steps: { metadata: boolean; options: boolean; answers: boolean };
      detail?: PlatformQuestionDetail;
      error?: string;
    }>(`/v1/platform/questions/${id}/full`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  publishPlatformQuestion: (id: string) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}/publish`, {
      method: "POST",
    }),

  archivePlatformQuestion: (id: string) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}/archive`, {
      method: "POST",
    }),

  // Pull a published/archived question back to draft so its content can
  // be corrected. Safe — tenant assessments deep-copy on adoption and
  // never read platform questions during a live sitting.
  revertPlatformQuestionToDraft: (id: string) =>
    request<{ ok: true; version: number }>(
      `/v1/platform/questions/${id}/revert-to-draft`,
      { method: "POST" },
    ),

  deletePlatformQuestion: (id: string) =>
    request<{ ok: true }>(`/v1/platform/questions/${id}`, {
      method: "DELETE",
    }),

  listQuestionBank: (opts?: {
    module?: string;
    level_code?: string;
    type?: string;
    q?: string;
    tag?: string;
  }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts ?? {})) {
      if (v) qs.set(k, v);
    }
    const query = qs.toString() ? `?${qs}` : "";
    return request<{ questions: PlatformQuestionSummary[] }>(
      `/v1/question-bank${query}`,
    );
  },

  getBankQuestion: (id: string) =>
    request<PlatformQuestionDetail>(`/v1/question-bank/${id}`),

  adoptBankQuestion: (sectionId: string, platformQuestionId: string) =>
    request<{ question_id: string; order_index: number }>(
      `/v1/question-bank/sections/${sectionId}/adopt`,
      {
        method: "POST",
        body: JSON.stringify({ platform_question_id: platformQuestionId }),
      },
    ),

  // ── Platform stimulus sets (super_admin) ──────────────────────────────
  // CRUD + membership for first-class stimulus sets. All five list/get/
  // create/update/delete/attach calls sit behind /v1/platform/sets
  // (requirePlatformAdmin). adoptStimulusSet is the one tenant-side call:
  // it creates a new section in a tenant assessment from a published set,
  // mirroring adoptBankQuestion's "copy into a tenant target" shape.
  listStimulusSets: (opts?: {
    module?: string;
    level_code?: string;
    paper_id?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.module) qs.set("module", opts.module);
    if (opts?.level_code) qs.set("level_code", opts.level_code);
    if (opts?.paper_id) qs.set("paper_id", opts.paper_id);
    if (opts?.q) qs.set("q", opts.q);
    if (opts?.limit != null) qs.set("limit", String(opts.limit));
    if (opts?.offset != null) qs.set("offset", String(opts.offset));
    const query = qs.toString() ? `?${qs}` : "";
    return request<{
      sets: StimulusSetSummary[];
      limit: number;
      offset: number;
    }>(`/v1/platform/sets${query}`);
  },

  getStimulusSet: (id: string) =>
    request<StimulusSetSummary>(`/v1/platform/sets/${id}`),

  createStimulusSet: (body: CreateStimulusSetBody) =>
    request<{ id: string; content_hash: string }>("/v1/platform/sets", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateStimulusSet: (id: string, patch: UpdateStimulusSetBody) =>
    request<{ ok: true }>(`/v1/platform/sets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteStimulusSet: (id: string) =>
    request<{ ok: true }>(`/v1/platform/sets/${id}`, { method: "DELETE" }),

  // Attach / reorder a question into a set. The server validates the
  // question's module + level match the set's (400 otherwise).
  attachQuestionToSet: (
    setId: string,
    body: { question_id: string; set_order?: number },
  ) =>
    request<{ ok: true }>(`/v1/platform/sets/${setId}/questions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Adopt a whole published set into a tenant assessment: creates one new
  // section from the set's stimulus + clones every published member in
  // set_order. Tenant-side, teacher+ auth (same chain as adoptBankQuestion).
  adoptStimulusSet: (assessmentId: string, platformSetId: string) =>
    request<{ section_id: string; member_count: number }>(
      `/v1/question-bank/assessments/${assessmentId}/adopt-set`,
      {
        method: "POST",
        body: JSON.stringify({ platform_set_id: platformSetId }),
      },
    ),

  beginOffboarding: (id: string, confirmationSlug: string) =>
    request<{ ok: true }>(
      `/v1/platform/organisations/${id}/offboard`,
      {
        method: "POST",
        body: JSON.stringify({ confirmation_slug: confirmationSlug }),
      },
    ),

  cancelOffboarding: (id: string) =>
    request<{ ok: true }>(
      `/v1/platform/organisations/${id}/cancel-offboarding`,
      { method: "POST" },
    ),

  setMemberRole: (
    orgId: string,
    userId: string,
    role: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student",
  ) =>
    request<{ ok: true; role?: string; unchanged?: boolean }>(
      `/v1/platform/organisations/${orgId}/members/${userId}/role`,
      { method: "POST", body: JSON.stringify({ role }) },
    ),

  offboardingStatus: (id: string) =>
    request<{
      status: string;
      export_ready: boolean;
      export_generated_at: string | null;
      export_size_bytes: number | null;
    }>(`/v1/platform/organisations/${id}/offboard-status`),

  exportDownloadUrl: (id: string) =>
    `/v1/platform/organisations/${id}/export`,

  finaliseDeletion: (id: string, confirmation_slug: string) =>
    request<{
      deleted_tenant_db: boolean;
      deleted_r2_objects: number;
      deleted_memberships: number;
    }>(`/v1/platform/organisations/${id}/finalise-deletion`, {
      method: "POST",
      body: JSON.stringify({ confirmation_slug }),
    }),

  studentDashboard: () => request<StudentDashboard>("/v1/dashboard/student"),
  // `period` selects the headline KPI window (defaults to 30 days server-side
  // when omitted). Mirrors the StatsPeriod union the Stats selector emits.
  teacherDashboard: (period?: StatsPeriod) =>
    request<TeacherDashboard>(
      `/v1/dashboard/teacher${period ? `?period=${period}` : ""}`,
    ),
  orgDashboard: (period?: StatsPeriod) =>
    request<OrgDashboard>(
      `/v1/dashboard/org${period ? `?period=${period}` : ""}`,
    ),
  exportUrl: (filters: {
    from?: string;
    to?: string;
    module?: string;
    level_code?: string;
    status?: string;
  }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v) qs.set(k, v);
    }
    const q = qs.toString();
    return `/v1/reports/attempts.csv${q ? `?${q}` : ""}`;
  },

  claimListeningReplay: (attemptId: string, sectionId: string) =>
    request<{
      section_id: string;
      max_replays: number;
      replays_used: number;
      remaining_replays: number;
    }>(
      `/v1/listening/attempts/${attemptId}/sections/${sectionId}/listen`,
      { method: "POST" },
    ),

  // Refund a replay claimed but never heard (the audio download failed). The
  // claim is server-side and happens before the blob can be fetched, so this
  // hands the replay back rather than burning it on a network blip.
  releaseListeningReplay: (attemptId: string, sectionId: string) =>
    request<{
      section_id: string;
      max_replays: number;
      replays_used: number;
      remaining_replays: number;
    }>(
      `/v1/listening/attempts/${attemptId}/sections/${sectionId}/release`,
      { method: "POST" },
    ),

  // Save the candidate's last-known playback offset (and whether the audio
  // finished) so an interrupted play can RESUME instead of restarting — without
  // claiming a new replay. Advisory + best-effort: the player throttles these
  // and ignores failures. Server-side this only UPDATEs an existing
  // listening_replays row (a replay was claimed); a save with nothing claimed
  // is a no-op and never touches the replay count.
  saveListeningPosition: (
    attemptId: string,
    sectionId: string,
    position_seconds: number,
    finished: boolean,
  ) =>
    request<{
      section_id: string;
      position_seconds: number;
      finished: boolean;
      replays_used: number;
    }>(
      `/v1/listening/attempts/${attemptId}/sections/${sectionId}/position`,
      { method: "POST", body: JSON.stringify({ position_seconds, finished }) },
    ),

  fetchListeningStimulusBlob: async (
    attemptId: string,
    sectionId: string,
  ): Promise<Blob> => {
    // Abort a stalled R2 download (30s) rather than hang the listening player
    // forever on a slow/dead connection. The deadline is per attempt, owned by
    // authedFetch, so the refresh-and-retry does not run unbounded inside a
    // timer that has already fired.
    try {
      // authedFetch: this is the candidate's own listening audio, fetched
      // per section DURING the exam. A listening paper outlives the 15-minute
      // token, so a later section used to 401 and the candidate simply had no
      // audio to answer from.
      const res = await authedFetch(
        `/v1/listening/attempts/${attemptId}/sections/${sectionId}/audio`,
        { timeoutMs: 30_000 },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new ApiError(
          res.status,
          body?.error?.code ?? "internal",
          body?.error?.message ?? res.statusText,
        );
      }
      return await res.blob();
    } catch (e) {
      // A blown deadline surfaces as an AbortError DOMException; map it to the
      // ApiError shape the listening player's error handling expects.
      if (e instanceof DOMException && e.name === "TimeoutError") {
        throw new ApiError(504, "timeout", "audio download timed out");
      }
      throw e;
    }
  },

  // Teacher/author preview of a section's uploaded stimulus audio. Hits the
  // teacher-authed stream route (GET /v1/listening/sections/:id/audio), which
  // mirrors uploadSectionAudio's auth — no attempt/replay context needed.
  // Returns a Blob the caller wraps in an object URL for an <audio> element.
  fetchSectionAudioBlob: async (sectionId: string): Promise<Blob> => {
    // authedFetch: a listening paper fetching a later section past the token's
    // 15th minute would otherwise 401 and leave the candidate with no audio.
    const res = await authedFetch(
      `/v1/listening/sections/${sectionId}/audio`,
    );
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        body?.error?.code ?? "internal",
        body?.error?.message ?? res.statusText,
      );
    }
    return await res.blob();
  },

  // ── Session Replays (owner console) ───────────────────────────
  // tenant_admin+ browse of recorded candidate sessions, mirroring the
  // submissions console. List/detail are metadata-only; the player streams
  // chunks progressively via fetchOrgReplayChunk so long recordings load.
  listOrgReplays: (
    filter: Record<string, unknown> = {},
  ): Promise<ReplayListResult> => {
    const q = toQueryString(filter);
    return request<ReplayListResult>(`/v1/org/replays${q ? `?${q}` : ""}`);
  },
  replaysSummary: (
    filter: Record<string, unknown> = {},
  ): Promise<ReplaySummary> => {
    const q = toQueryString(filter);
    return request<ReplaySummary>(`/v1/org/replays/summary${q ? `?${q}` : ""}`);
  },
  // Absolute URL for the CSV export (downloaded via downloadAuthed with a token).
  replaysExportUrl: (filter: Record<string, unknown> = {}): string => {
    const q = toQueryString(filter);
    return `${API_BASE_URL}/v1/org/replays/export.csv${q ? `?${q}` : ""}`;
  },
  orgReplayDetail: (attemptId: string): Promise<ReplayDetail> =>
    request<ReplayDetail>(`/v1/org/replays/${encodeURIComponent(attemptId)}`),
  fetchOrgReplayChunk: (attemptId: string, seq: number): Promise<unknown[]> =>
    fetchReplayChunk(`/v1/org/replays/${encodeURIComponent(attemptId)}/chunk/${seq}`),
  // Examiner per-attempt variant (marking router) — same progressive player.
  markingReplayMeta: (attemptId: string): Promise<ReplayDetail> =>
    request<ReplayDetail>(
      `/v1/marking/attempts/${encodeURIComponent(attemptId)}/recording/meta`,
    ),
  fetchMarkingReplayChunk: (attemptId: string, seq: number): Promise<unknown[]> =>
    fetchReplayChunk(
      `/v1/marking/attempts/${encodeURIComponent(attemptId)}/recording/chunk/${seq}`,
    ),

  // ── Submissions Console ───────────────────────────────────────

  // List submissions with optional filters. Array values (e.g. status[])
  // are appended individually; null/empty values are skipped.
  listSubmissions: (
    filter: Record<string, unknown> = {},
  ): Promise<SubmissionListResult> => {
    const q = submissionsQuery(filter);
    return request<SubmissionListResult>(`/v1/submissions${q ? `?${q}` : ""}`);
  },

  // Summary counts by status (for the header panel above the list).
  submissionsSummary: (
    filter: Record<string, unknown> = {},
  ): Promise<SubmissionSummary> => {
    const q = submissionsQuery(filter);
    return request<SubmissionSummary>(
      `/v1/submissions/summary${q ? `?${q}` : ""}`,
    );
  },

  // Per-submission detail (responses + marking sessions + retention).
  getSubmission: (attemptId: string): Promise<SubmissionDetail> =>
    request<SubmissionDetail>(
      `/v1/submissions/${encodeURIComponent(attemptId)}`,
    ),

  // Chronological audit timeline for a single submission.
  getSubmissionTimeline: (
    attemptId: string,
  ): Promise<SubmissionTimelineEvent[]> =>
    request<SubmissionTimelineEvent[]>(
      `/v1/submissions/${encodeURIComponent(attemptId)}/timeline`,
    ),

  // Release held results for the assessment this submission belongs to.
  // tenant_admin+ only — the server enforces RBAC.
  releaseSubmissionResults: (
    attemptId: string,
  ): Promise<{ ok: boolean; released: boolean }> =>
    request<{ ok: boolean; released: boolean }>(
      `/v1/submissions/${encodeURIComponent(attemptId)}/release-results`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  // Build the download URL for a CSV or JSON export. Returns a full URL
  // (including API_BASE_URL) so it can be used as an <a href> directly.
  // Array values (e.g. status[]) are appended individually.
  submissionsExportUrl: (
    filter: Record<string, unknown> = {},
    fmt: "csv" | "json",
  ): string => {
    const q = submissionsQuery(filter);
    return `${API_BASE_URL}/v1/submissions/export.${fmt}${q ? `?${q}` : ""}`;
  },

  // Returns the absolute URL for fetching the evidence bundle for a
  // single submission. Auth-gated — must be fetched with a Bearer token
  // (use downloadAuthed).
  submissionEvidenceUrl: (attemptId: string): string =>
    `${API_BASE_URL}/v1/submissions/${encodeURIComponent(attemptId)}/evidence`,

  // Fetch the signed evidence manifest as a typed object (vs. the raw-file
  // download above). Used to render the comprehensive evidence PDF client-side
  // — same payload, but parsed so the PDF generator can lay it out.
  getSubmissionEvidence: (
    attemptId: string,
  ): Promise<SubmissionEvidenceManifest> =>
    request<SubmissionEvidenceManifest>(
      `/v1/submissions/${encodeURIComponent(attemptId)}/evidence`,
    ),

  // Mint (idempotent) a certificate for this submission's candidate, acting as
  // org staff. Returns the cert_no the verify page can render. moderator+ only —
  // the server enforces RBAC and records the staff actor in the audit trail.
  issueSubmissionCertificate: (
    attemptId: string,
  ): Promise<{
    cert_no: string;
    issued_at: string;
    valid_until: string;
    candidate_name: string;
    has_candidate_photo: boolean;
    award_line: string | null;
    institution_id: string;
    title: string;
    module: string;
    level_code: string;
    duration_seconds: number;
    is_test: boolean;
  }> =>
    request<{
      cert_no: string;
      issued_at: string;
      valid_until: string;
      candidate_name: string;
      has_candidate_photo: boolean;
      award_line: string | null;
      institution_id: string;
      title: string;
      module: string;
      level_code: string;
      duration_seconds: number;
      is_test: boolean;
    }>(`/v1/submissions/${encodeURIComponent(attemptId)}/certificate`, {
      method: "POST",
    }),

  // Fetch a candidate's recorded audio answer for org-side review. The
  // <audio> element can't send a Bearer header, so we fetch the moderator-gated
  // stream here and the caller wraps the Blob in an object URL. Mirrors
  // fetchSectionAudioBlob.
  fetchSubmissionAudioBlob: async (
    attemptId: string,
    questionId: string,
  ): Promise<Blob> => {
    const res = await authedFetch(
      `/v1/submissions/${encodeURIComponent(
        attemptId,
      )}/responses/${encodeURIComponent(questionId)}/audio`,
    );
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        body?.error?.code ?? "internal",
        body?.error?.message ?? res.statusText,
      );
    }
    return await res.blob();
  },
};

export interface WritingSubmitResult {
  attempt_id: string;
  status: "awaiting_review";
  responses: Array<{
    response_id: string;
    question_id: string;
    word_count: number;
    min_words: number;
    max_words: number;
    meets_min: boolean;
    meets_max: boolean;
  }>;
}

export interface GrammarCheckResult {
  model: string;
  stub: boolean;
  issues: Array<{
    offset: number;
    length: number;
    text: string;
    suggestion: string;
    explanation: string;
    kind: "spelling" | "grammar" | "style" | "other";
  }>;
  summary: string;
}

export interface QuestionAssistResult {
  model: string;
  stub: boolean;
  improved_prompt: string | null;
  suggested_options: Array<{ text: string; is_correct: boolean }> | null;
  suggested_answers: string[] | null;
  notes: string;
}

export interface QuestionFlag {
  id: string;
  platform_question_id: string;
  flagged_by_user_id: string;
  flagged_by_email: string | null;
  flagged_at: string;
  category:
    | "factual_error"
    | "offensive_content"
    | "bias_or_discrimination"
    | "accessibility"
    | "curriculum_drift"
    | "duplicate"
    | "other";
  reason: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution: "upheld" | "dismissed" | "duplicate" | null;
  resolution_note: string | null;
}

export interface QuestionBankBookletQuestion {
  number: number;
  level_code: string;
  module: string;
  type: string;
  prompt: string;
  stimulus_text: string;
  stimulus_image_url: string | null;
  marks: number;
  tags: string[];
  transcript: string;
  options: Array<{ text: string; is_correct: boolean }>;
  answers: Array<{ accepted_answer: string; marks: number }>;
}

export interface QuestionBankBooklet {
  generated_at: string;
  total: number;
  filters: {
    status: string;
    module: string | null;
    level_code: string | null;
    programme_id: string | null;
  };
  questions: QuestionBankBookletQuestion[];
}

export interface PlatformQuestionSummary {
  id: string;
  // Includes the HEDA bands D1–D3 the API can return (see shared
  // LEVEL_CODES) — not just the Functional Skills E/L levels.
  level_code: "E1" | "E2" | "E3" | "L1" | "L2" | "D1" | "D2" | "D3";
  module:
    | "reading"
    | "writing"
    | "listening"
    | "speaking"
    | "heda_english"
    | "heda_math"
    | "heda_ict";
  type:
    | "mcq_single"
    | "mcq_multi"
    | "true_false"
    | "short_answer"
    | "long_answer"
    | "essay"
    | "speaking_response"
    | "numeric_answer"
    | "match_pairs"
    | "multi_part";
  prompt: string;
  marks: number;
  tags: string[];
  status: "draft" | "published" | "archived";
  version: number;
  adopted_count?: number;
  programme_id?: string | null;
  // Quality scoring (migration 0034 + nightly cron). Null until scored.
  quality_score?: number | null;
  quality_bucket?: "A" | "B" | "C" | "D" | null;
  quality_issues?: string[];
  quality_checked_at?: string | null;
  created_at: string;
  updated_at?: string;
  // List-view enrichment (present on rows from listPlatformQuestions).
  paper_id?: string | null;
  stimulus_kind?: "none" | "text" | "image";
  options_count?: number;
  answers_count?: number;
  // Stimulus-set membership (migration 0040). null for standalone
  // questions; set_order positions a member within its set. Populated on
  // detail rows (and on summaries fetched via the set_id filter, where we
  // tag them client-side from the set list).
  set_id?: string | null;
  set_order?: number | null;
}

export interface PlatformQuestionDetail extends PlatformQuestionSummary {
  stimulus_text: string;
  stimulus_image_hash?: string | null;
  stimulus_image_type?: string | null;
  content_hash?: string | null;
  normalised_prompt_hash?: string | null;
  submitted_for_review_at?: string | null;
  submitted_by_user_id?: string | null;
  approved_at?: string | null;
  approved_by_user_id?: string | null;
  review_notes?: string | null;
  alt_text?: Record<string, string>;
  transcript?: string;
  extra_time_allowed?: number;
  content_standards?: string[];
  // Per-question constraints — writing word limits, speaking
  // timing, etc. Carried through to tenant questions.metadata_json
  // on adoption. Empty object {} for modules that don't use it.
  metadata?: Record<string, unknown>;
  options: Array<{
    id: string;
    order_index: number;
    text: string;
    is_correct: boolean;
    pair_group?: number | null;
    pair_side?: number | null;
    // First-class option image (master 0041): a match_pairs item can be a
    // picture. Content hash served via /v1/question-assets/<hash>; null for
    // text-only options.
    image_hash?: string | null;
  }>;
  answers: Array<{
    id: string;
    accepted_answer: string;
    match_type: "exact" | "case_insensitive" | "regex" | "fuzzy";
    marks: number;
    tolerance_absolute?: number | null;
    tolerance_percent?: number | null;
    units?: string | null;
    significant_digits?: number | null;
  }>;
  // Bundle-import + audio fields (migrations 0030/0033). Present on
  // questions that came from a bundle import; null otherwise.
  paper_id?: string | null;
  paper_question_order?: number | null;
  word_limit?: number | null;
  skill?: string | null;
  audio_path?: string | null;
  audio_sha256?: string | null;
  audio_duration_seconds?: number | null;
  audio_provenance?: "real" | "synthesised" | "official" | null;
  source_paper?: {
    paper_id: string;
    awarding_body: string | null;
    session: string | null;
    paper_code: string | null;
    source_url: string | null;
    is_ai_generated: boolean;
    import_batch: string | null;
    parse_confidence: number | null;
  } | null;
  // Nested parent stimulus set (migration 0040). Present only for members
  // (set_id NOT NULL). The raw stimulus_text/image/audio fields above remain
  // the question's own (empty for members) values; renderers resolve the
  // passage/audio/image to show via this `set` (see effectiveStimulus).
  set?: {
    id: string;
    title: string;
    stimulus_type: "passage" | "audio" | "prompt" | "none";
    stimulus_text: string;
    stimulus_image_hash: string | null;
    stimulus_image_type: string | null;
    audio_path: string | null;
    audio_sha256: string | null;
    audio_duration_seconds: number | null;
    audio_provenance: "real" | "synthesised" | "official" | null;
    transcript: string;
  } | null;
}

export interface CreatePlatformQuestionBody {
  level_code: "E1" | "E2" | "E3" | "L1" | "L2" | "D1" | "D2" | "D3";
  module:
    | "reading"
    | "writing"
    | "listening"
    | "speaking"
    | "heda_english"
    | "heda_math"
    | "heda_ict";
  type:
    | "mcq_single"
    | "mcq_multi"
    | "true_false"
    | "short_answer"
    | "long_answer"
    | "essay"
    | "speaking_response"
    | "numeric_answer"
    | "match_pairs"
    | "multi_part";
  prompt: string;
  stimulus_text?: string;
  marks?: number;
  tags?: string[];
  programme_id?: string;
  parent_question_id?: string;
  content_standards?: string[];
  options?: Array<{
    order_index: number;
    text: string;
    is_correct: boolean;
    pair_group?: number;
    pair_side?: 0 | 1;
  }>;
  answers?: Array<{
    accepted_answer: string;
    match_type?: "exact" | "case_insensitive" | "regex" | "fuzzy";
    marks?: number;
    tolerance_absolute?: number;
    tolerance_percent?: number;
    units?: string;
    significant_digits?: number;
  }>;
  // Per-question constraints carried into the tenant copy at
  // adoption — writing word limits and speaking timing. Server
  // validates via platformQuestionMetadataSchema (strict object).
  metadata?: {
    prompt_type?: string;
    min_words?: number;
    max_words?: number;
    task_type?: string;
    prep_seconds?: number;
    min_seconds?: number;
    max_seconds?: number;
  };
  // Stimulus-set attachment (migration 0040). A question can belong to at
  // most one set; omit both for a standalone question. The server validates
  // the question's module + level match the set's.
  set_id?: string | null;
  set_order?: number | null;
}

// ── Platform stimulus sets (migration 0040) ─────────────────────────────
// A set stores a passage / audio clip / writing-or-speaking prompt once;
// many questions attach to it many-to-one. The list/detail shapes mirror
// the master-DB StimulusSetRow returned by /v1/platform/sets.
export interface StimulusSet {
  id: string;
  title: string;
  module:
    | "reading"
    | "writing"
    | "listening"
    | "speaking"
    | "heda_english"
    | "heda_math"
    | "heda_ict";
  level_code: "E1" | "E2" | "E3" | "L1" | "L2" | "D1" | "D2" | "D3";
  programme_id: string | null;
  stimulus_type: "passage" | "audio" | "prompt" | "none";
  stimulus_text: string;
  stimulus_image_hash: string | null;
  stimulus_image_type: string | null;
  /** First-class SHARED stimulus video (additive). Content hash served
   *  read-only by capability via /v1/question-assets/<hash>; null when none. */
  stimulus_video_hash: string | null;
  stimulus_video_type: string | null;
  audio_path: string | null;
  audio_sha256: string | null;
  audio_duration_seconds: number | null;
  audio_provenance: string | null;
  transcript: string;
  paper_id: string | null;
  set_order: number | null;
  status: "draft" | "published" | "archived";
  version: number;
  content_hash: string | null;
  created_at: string;
  updated_at: string;
  created_by_user_id: string;
}

// List rows carry the live member_count (correlated subquery, server-side).
export interface StimulusSetSummary extends StimulusSet {
  member_count: number;
}

export interface CreateStimulusSetBody {
  title: string;
  module: StimulusSet["module"];
  level_code: StimulusSet["level_code"];
  programme_id?: string;
  stimulus_type: StimulusSet["stimulus_type"];
  stimulus_text?: string;
  stimulus_image_hash?: string;
  stimulus_image_type?: string;
  stimulus_video_hash?: string;
  stimulus_video_type?: string;
  audio_path?: string;
  audio_sha256?: string;
  audio_duration_seconds?: number;
  audio_provenance?: string;
  transcript?: string;
  paper_id?: string;
  set_order?: number;
}

// PATCH payload — module + level_code are fixed at creation and omitted.
export interface UpdateStimulusSetBody {
  title?: string;
  stimulus_type?: StimulusSet["stimulus_type"];
  stimulus_text?: string;
  stimulus_image_hash?: string | null;
  stimulus_image_type?: string | null;
  stimulus_video_hash?: string | null;
  stimulus_video_type?: string | null;
  audio_path?: string | null;
  audio_sha256?: string | null;
  audio_duration_seconds?: number | null;
  audio_provenance?: string | null;
  transcript?: string;
  paper_id?: string | null;
  set_order?: number | null;
  status?: "draft" | "published" | "archived";
}

export interface OrgUsage {
  plan: {
    code: "starter" | "growth" | "enterprise";
    display_name: string;
    limits: {
      max_published_assessments: number;
      max_attempts_per_month: number;
    };
    overrides: {
      max_published_assessments?: number;
      max_attempts_per_month?: number;
    } | null;
    base_limits: {
      max_published_assessments: number;
      max_attempts_per_month: number;
    };
  };
  usage: {
    published_assessments: number;
    attempts_this_month: number;
  };
  caps: {
    publish: { allowed: boolean; limit: number; current: number; reason?: string };
    attempts: { allowed: boolean; limit: number; current: number; reason?: string };
  };
  // The metered overage/cap block. null for grandfathered tenants — only
  // populated once the org is on metered billing (metered_billing_active=1).
  // Mirrors apps/api/src/services/usage.ts MeteredUsageSummary.
  metered: MeteredUsageSummary | null;
}

// Mirrors apps/api/src/services/usage.ts MeteredUsageSummary. Surfaced on both
// OrgUsage.metered and Billing.metered so the metered usage panel + spend-cap
// editor read the same shape.
export interface MeteredUsageSummary {
  mode: "subscription" | "payg";
  billing_period: string;
  /**
   * ISO-8601 UTC start of the current billing period (first instant of the
   * calendar month). Drives days-elapsed / days-left + the spend projection.
   */
  period_start: string;
  /**
   * ISO-8601 UTC end of the current billing period (first instant of the NEXT
   * calendar month — exclusive).
   */
  period_end: string;
  /** Billable module-attempts on the ledger for the current period. */
  usage: number;
  /** Included module-attempts the plan covers (the quota). -1 ⇒ unlimited. */
  quota: number;
  /** Billable units beyond the quota (0 while under quota). */
  overage_units: number;
  /** Per-unit overage price, in pence (display/projection). */
  overage_unit_price_pence: number;
  /** Projected overage spend so far this period, in pence. */
  overage_spend_pence: number;
  /** Effective hard spend cap, in pence. null ⇒ unlimited (never blocked). */
  hard_spend_cap_pence: number | null;
  /** The org's own override (null ⇒ falling back to the plan default). */
  hard_spend_cap_pence_override: number | null;
  /** The plan's default cap, in pence (null ⇒ plan imposes none). */
  plan_default_hard_spend_cap_pence: number | null;
  /**
   * Percent of the effective cap the projected spend has reached, 0–100
   * (clamped). null when there's no cap (unlimited).
   */
  percent_to_cap: number | null;
}

export interface PlatformTenantSummary {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  schema_version: number;
  has_tenant_db: number | boolean;
  custom_domain: string | null;
  created_at: string;
  updated_at: string;
  member_count: number;
}

export interface PlatformTenantDetail extends PlatformTenantSummary {
  tenant_d1_database_id: string | null;
  settings_json: string;
  /** Platform target schema version; gap vs `schema_version` is drift. */
  current_schema_version: number;
  retention_days: number | null;
  plan_overrides: {
    max_published_assessments?: number;
    max_attempts_per_month?: number;
  } | null;
  roles: Array<{ role: string; count: number }>;
  admins: Array<{
    user_id: string;
    user_email: string | null;
    user_full_name: string | null;
    is_founder: number;
  }>;
  recent_members: Array<{
    id: string;
    role: string;
    status: string;
    joined_at: string;
    user_id: string;
    user_email: string | null;
    user_full_name: string | null;
    is_founder: number;
  }>;
  billing: {
    mode: string;
    metered_active: boolean;
    hard_spend_cap_pence: number | null;
    metered_activated_at: string | null;
    billing_suspended_at: string | null;
    stripe_customer_id: string | null;
  };
  subscription: {
    status: string;
    plan_code: string;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    stripe_subscription_id: string;
  } | null;
  usage: {
    latest_period: string | null;
    latest_period_quantity: number;
    parked_events: number;
  };
}

/** One distinct fault in the platform error inbox. Keyed by fingerprint, so
 *  `count` is how many times it has happened, not how many rows exist. */
export interface PlatformError {
  fingerprint: string;
  level: "error" | "warn";
  /** "http", "client", or "queue:<job kind>". */
  source: string;
  sample_message: string;
  sample_path: string | null;
  sample_request_id: string | null;
  release: string | null;
  count: number;
  first_seen: string;
  last_seen: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  notified_at: string | null;
}

export interface PlatformMetrics {
  tenants: Record<string, number>;
  total_users: number;
  memberships: Record<string, number>;
  recent_audit: Array<{
    id: string;
    actor_user_id: string | null;
    action: string;
    target_type: string | null;
    target_id: string | null;
    created_at: string;
  }>;
  usage_24h?: {
    logins_succeeded: number;
    logins_failed: number;
    tenant_provisions: number;
    impersonations: number;
    exports_downloaded: number;
  };
}

// ── Transcript template ───────────────────────────────────────
// Mirrors the shared Zod schema in
// packages/shared/src/programmes/index.ts. Kept in lockstep — if
// you add / rename a key there, update here.
export type TranscriptTemplateKey =
  | "formal"
  | "modern"
  | "minimal"
  | "classic"
  | "elegant"
  | "bold"
  | "transcript"
  | "registrar"
  | "ledger";

export type BorderStyle = "none" | "thin" | "double" | "ornamental";
// "auto" defers to the layout preset's own typeface, which is what makes the
// nine presets differ typographically. Mirrors CANDIDATE_NAME_FONTS in
// packages/shared/src/programmes.
export type CandidateNameFont = "auto" | "serif" | "sans" | "display";

export interface TranscriptTemplate {
  template_key: TranscriptTemplateKey;
  header_text: string;
  introduction_text: string;
  closing_text: string;
  signature_label: string;
  signatory_name: string;
  signatory_title: string;
  awarding_body_name: string;
  award_label: string;
  footer_text_override: string | null;
  show_prior_attempts: boolean;
  show_per_question: boolean;
  show_qr_verification: boolean;
  show_signature_block: boolean;
  accent_hex: string | null;
  border_style: BorderStyle;
  show_watermark: boolean;
  candidate_name_font: CandidateNameFont;
  /** Multiplier on the preset's logo box. 1 = the preset's own size. */
  logo_scale: number;
  show_component_table: boolean;
  show_candidate_email: boolean;
  show_issued_dates: boolean;
  show_issuer_name: boolean;
  show_issuer_name_footer: boolean;
  // Certificate branding. Deliberately separate from tenant branding: a
  // certificate is issued by the assessment organisation, not by the college
  // whose tenant it sits in. Blank/null falls back to PLATFORM branding.
  issuer_name: string;
  issuer_logo_hash: string | null;
  legal_entity: string;
  company_number: string;
  // Default validation signatories for this programme. A sitting that sets its
  // own still wins.
  validation_assessment: AssessmentValidation | null;
  validation_sitting: SittingValidation | null;
  validity_years: number;
}

export interface SittingRow {
  id: string;
  title: string;
  level_code: string;
  status: "draft" | "published" | "archived";
  opens_at: string | null;
  closes_at: string | null;
  session_cap_seconds: number | null;
  order_mode: "free" | "linear";
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  /** Raw "Sitting Configuration" validation blob (Exams Officer + Lead
   *  Invigilator) — parse with parseSittingValidation from @exambase/shared. */
  validation_json?: string | null;
  /** Module codes for the assessments in this sitting, in slot
   *  (order_index) order. Used by SittingsList to render module-icon
   *  strips per card. Absent for detail-only SELECTs that don't
   *  compute it. */
  modules?: string[];
  /** DISTINCT candidates currently mid-sitting (in_progress/paused run).
   *  Rendered as the "N sitting now" signal per card. Absent on
   *  detail-only SELECTs. NOTE: SittingDetail re-declares this as a
   *  required run-row tally for the edit/delete guard — different metric,
   *  same name. */
  active_run_count?: number;
  /** DISTINCT candidates who finished every slot in this sitting. Rendered
   *  as the "N completed" signal per card. Absent on detail-only SELECTs. */
  completed_run_count?: number;
}

export interface SittingSlotDetail {
  id: string;
  sitting_id: string;
  assessment_id: string;
  order_index: number;
  created_at: string;
  assessment_title: string;
  assessment_module: string;
  assessment_duration_seconds: number;
  assessment_status: string;
}

export interface SittingDetail extends SittingRow {
  slots: SittingSlotDetail[];
  /** Number of candidate runs currently mid-sitting (in_progress or
   *  paused). When > 0 the editor locks structural edits + delete to
   *  protect live sessions. */
  active_run_count: number;
}

export interface SittingHubSlot {
  slot_id: string;
  assessment_id: string;
  assessment_title: string;
  assessment_module: string;
  order_index: number;
  duration_seconds: number;
  elapsed_seconds: number;
  time_remaining_seconds: number;
  status: "not_started" | "in_progress" | "paused" | "submitted";
  attempt_id: string | null;
  last_active_at: string | null;
}

export interface SittingHub extends SittingRow {
  slots: SittingHubSlot[];
}

export interface SittingResultSlot {
  slot_id: string;
  assessment_id: string;
  assessment_title: string;
  assessment_module: string;
  order_index: number;
  duration_seconds: number;
  attempt_id: string | null;
  status: "not_started" | "in_progress" | "paused" | "submitted";
  submitted_at: string | null;
  attempt_status: string | null;
  score_raw: number | null;
  score_max: number | null;
  score_percent: number | null;
  passed: boolean | null;
  band: string | null;
  // Per-assessment reporting/grading config, so the result page can
  // pick band-vs-percent and suppress the verdict on raw_score slots.
  score_reporting?: "percent" | "band";
  grading_mode?: "pass_fail" | "raw_score";
  time_taken_seconds: number | null;
}

export interface SittingResult extends SittingRow {
  slots: SittingResultSlot[];
  all_submitted: boolean;
  overall_percent: number | null;
  passed_slots: number;
  scored_slots: number;
  /** Any slot is a mock (practice) paper. The server refuses to issue a
   *  certificate for such a sitting, so the result page hides the CTA. */
  has_mock_slot?: boolean;
  /** Raw "Assessment Configuration" validation blob (Assessor + Internal
   *  Verifier) from the sitting's assessments — parse with
   *  parseAssessmentValidation from @exambase/shared. */
  assessment_validation_json?: string | null;
}

// Staff completion overview (read-only aggregate). Distinct from
// SittingResult: that is a SINGLE candidate's own breakdown; this aggregates
// across EVERY candidate so staff can see how many of M enrolled candidates
// have finished, per-slot completion + pass tallies, and a per-candidate list.
export interface SittingOverviewSlot {
  slot_id: string;
  assessment_id: string;
  assessment_title: string;
  assessment_module: string;
  order_index: number;
  /** DISTINCT candidates whose run for this slot is submitted. */
  submitted_count: number;
  /** Of the submitted, how many have a finalised verdict (not awaiting). */
  scored_count: number;
  /** Of the scored, how many passed. band/raw_score components never count. */
  passed_count: number;
}

export interface SittingOverviewCandidate {
  student_user_id: string;
  /** Resolved from the master users table; null when the user is deleted. */
  full_name: string | null;
  email: string | null;
  submitted_slots: number;
  total_slots: number;
  all_submitted: boolean;
}

export interface SittingResultsOverview {
  id: string;
  title: string;
  level_code: string;
  status: "draft" | "published" | "archived";
  opens_at: string | null;
  closes_at: string | null;
  session_cap_seconds: number | null;
  /** Number of slots (components) — the "M" denominator. */
  slot_count: number;
  /** DISTINCT candidates with any run on this sitting. */
  candidate_count: number;
  /** DISTINCT candidates who have submitted every slot. */
  completed_count: number;
  slots: SittingOverviewSlot[];
  candidates: SittingOverviewCandidate[];
}

// Staff live-monitor (read-only invigilation view). Distinct from the
// completion overview: that aggregates finished/scored counts; this is the
// "right now" view — per candidate, per slot, the live status + time remaining
// + paused flag, derived from sitting_runs without mutating anything.
export interface SittingMonitorSlotStatus {
  slot_id: string;
  assessment_id: string;
  assessment_title: string;
  assessment_module: string;
  order_index: number;
  duration_seconds: number;
  status: "not_started" | "in_progress" | "paused" | "submitted";
  /** Convenience flag: true iff status === 'paused'. */
  paused: boolean;
  /** Wall-clock-aware remaining seconds (in_progress tick folded in), 0..dur. */
  time_remaining_seconds: number;
}

export interface SittingMonitorCandidate {
  student_user_id: string;
  /** Resolved from the master users table; null when the user is deleted. */
  full_name: string | null;
  email: string | null;
  /** One entry per sitting slot, in order_index order. */
  slots: SittingMonitorSlotStatus[];
}

export interface SittingLiveMonitor {
  id: string;
  title: string;
  level_code: string;
  status: "draft" | "published" | "archived";
  opens_at: string | null;
  closes_at: string | null;
  session_cap_seconds: number | null;
  /** The sitting's slot line-up (order_index order) — the monitor's columns. */
  slots: Array<{
    slot_id: string;
    assessment_id: string;
    assessment_title: string;
    assessment_module: string;
    order_index: number;
    duration_seconds: number;
  }>;
  /** One row per DISTINCT candidate with any run on the sitting. */
  candidates: SittingMonitorCandidate[];
}

export interface AttemptOverrideView {
  id: string;
  assessment_id: string;
  user_id: string;
  candidate_email: string | null;
  candidate_full_name: string | null;
  extra_time_seconds: number;
  extra_attempts: number;
  opens_at: string | null;
  closes_at: string | null;
  note: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export type ResultReleasePolicy =
  | "immediate"
  | "manual_hold"
  | "scheduled"
  | "on_close"
  | "on_marked"
  | "never";

export interface AuthoringAssessment {
  id: string;
  title: string;
  instructions?: string;
  /** Raw "Assessment Configuration" validation blob (Assessor + Internal
   *  Verifier) — parse with parseAssessmentValidation from @exambase/shared. */
  validation_json?: string | null;
  module: string;
  level_code: string;
  duration_seconds: number;
  pass_threshold_percent: number;
  grading_mode?: "pass_fail" | "raw_score";
  max_attempts: number;
  status: "draft" | "published" | "archived";
  version: number;
  course_id: string | null;
  is_template?: number;
  created_at: string;
  // Enterprise settings (see migrations/tenant/0009). Server returns
  // null / 0 / 'immediate' defaults for papers created before v10.
  opens_at?: string | null;
  closes_at?: string | null;
  late_submission_grace_seconds?: number;
  shuffle_questions?: number;
  shuffle_options?: number;
  result_release_mode?: "immediate" | "manual_hold";
  /** Supersedes result_release_mode when set. See services/resultRelease. */
  result_release_policy?: ResultReleasePolicy | null;
  /** Target instant for the "scheduled" policy. */
  results_release_at?: string | null;
  results_released_at?: string | null;
  candidate_can_see_score?: number;
  candidate_can_see_per_question?: number;
  candidate_can_see_correct_answers?: number;
  candidate_can_see_feedback?: number;
  score_reporting?: "percent" | "band";
  band_boundaries_json?: string | null;
  // Pool draw: when set, each attempt picks this many random sections
  // from the section pool. Null = include every section (legacy).
  draw_sections_count?: number | null;
  // Question-level pool: when set, each attempt draws this many random
  // questions from the assessment. Null = show every question (legacy).
  display_question_count?: number | null;
  // Author flag — when 1, the take-page preflight (identity / rules /
  // honour code) is bypassed and the candidate lands on the question
  // screen immediately. 0/null keeps the default 3-step gate.
  skip_preflight?: number | null;
  // Author flag — when 1, the exam runner records the candidate's
  // consent-gated on-screen session for invigilation review. 0/null = OFF.
  record_session?: number | null;
  // Author flag — when 1 the exam runner allows copy/paste/context-menu
  // (no block, no incident). 0/null keeps the strict block.
  allow_copy_paste?: number | null;
  // Author flag — when 1 this is a MOCK (practice) paper. It runs and marks
  // identically to a real exam, but no certificate can ever be issued from it
  // and it is excluded from every dashboard statistic.
  is_mock?: number | null;
  sections: Array<AuthoringSection>;
}

export interface AuthoringSection {
  id: string;
  order_index: number;
  title: string;
  instructions: string;
  stimulus_type: string;
  stimulus_text: string;
  stimulus_r2_key: string | null;
  stimulus_content_type?: string | null;
  stimulus_duration_seconds?: number | null;
  /** Listening replay limit — how many times a candidate may play the
   *  section audio during an attempt. Authored on create and editable
   *  from the listening section editor. */
  max_replays?: number | null;
  /** First-class shared section image/video (carried from an adopted set);
   *  served via the capability-by-hash route so the teacher preview can show
   *  the same asset the candidate sees. */
  stimulus_image_hash?: string | null;
  stimulus_video_hash?: string | null;
  /** Accessibility text for the section's stimulus image/audio
   *  (WCAG 1.1.1). Editable inline from the module sub-editors. */
  alt_text?: string;
  time_limit_seconds: number | null;
  questions: Array<AuthoringQuestion>;
}

export interface AuthoringQuestion {
  id: string;
  section_id: string;
  order_index: number;
  type:
    | "mcq_single"
    | "mcq_multi"
    | "true_false"
    | "short_answer"
    | "long_answer"
    | "essay"
    | "speaking_response"
    | "numeric_answer";
  prompt: string;
  marks: number;
  metadata: Record<string, unknown>;
  options: Array<{
    id: string;
    question_id: string;
    order_index: number;
    text: string;
    is_correct: number;
  }>;
  answers: Array<{
    id: string;
    question_id: string;
    accepted_answer: string;
    match_type: string;
    marks: number;
    tolerance_absolute?: number | null;
    tolerance_percent?: number | null;
    units?: string | null;
    significant_digits?: number | null;
  }>;
  platform_update: {
    adopted_version: number;
    available_version: number;
  } | null;
}

export interface CreateAssessmentBody {
  title: string;
  instructions?: string;
  level_code: "E1" | "E2" | "E3" | "L1" | "L2" | "D1" | "D2" | "D3";
  duration_seconds: number;
  pass_threshold_percent?: number;
  grading_mode?: "pass_fail" | "raw_score";
  max_attempts?: number;
}

export interface CreateReadingSectionBody {
  order_index: number;
  title: string;
  instructions?: string;
  stimulus_text?: string;
  time_limit_seconds?: number | null;
}

export interface CreateReadingQuestionBody {
  order_index: number;
  type: "mcq_single" | "mcq_multi" | "true_false" | "short_answer";
  prompt: string;
  marks: number;
  options?: Array<{ order_index: number; text: string; is_correct: boolean }>;
  answers?: Array<{
    accepted_answer: string;
    match_type: "exact" | "case_insensitive" | "regex" | "fuzzy";
    marks: number;
  }>;
}

export interface CreateWritingPromptBody {
  order_index: number;
  title: string;
  instructions?: string;
  prompt_text: string;
  prompt_type:
    | "email"
    | "letter"
    | "report"
    | "article"
    | "story"
    | "review"
    | "other";
  min_words: number;
  max_words: number;
  marks?: number;
  question_type?: "long_answer" | "essay";
}

export interface CreateSpeakingPromptBody {
  order_index: number;
  title: string;
  instructions?: string;
  prompt_text: string;
  task_type:
    | "monologue"
    | "interview"
    | "discussion"
    | "presentation"
    | "role_play"
    | "other";
  min_seconds: number;
  max_seconds: number;
  prep_seconds?: number;
  marks?: number;
}

export interface CreateRubricBody {
  name?: string;
  scale_max?: number;
  criteria: Array<{
    name: string;
    description?: string;
    weight?: number;
  }>;
}

export interface StudentDashboard {
  user_id: string;
  module_summary: Array<{
    module: string;
    attempts_count: number;
    scored_count: number;
    awaiting_review_count: number;
    // Score of the most-recent scored attempt per module. Policy
    // is "last attempt stands" for the final transcript; a retake
    // supersedes the previous try.
    last_score_percent: number | null;
    best_score_percent: number | null;
    last_attempt_at: string | null;
    passed_count: number;
    // Band + reporting mode of the latest scored attempt for the
    // module, so the transcript can show a band instead of a percent.
    last_band: string | null;
    last_score_reporting: "percent" | "band" | null;
    // Scored attempts that carry an actual pass/fail verdict
    // (passed IS NOT NULL). raw_score papers have verdict_count <
    // scored_count, so the UI can hide the pass rate.
    verdict_count: number;
  }>;
  recent_attempts: Array<{
    attempt_id: string;
    assessment_id: string;
    assessment_title: string;
    module: string;
    level_code: string;
    status: string;
    score_percent: number | null;
    passed: boolean | null;
    band: string | null;
    score_reporting: "percent" | "band";
    grading_mode: "pass_fail" | "raw_score";
    started_at: string;
    submitted_at: string | null;
  }>;
  // Distinct assessment ids the student has any live or completed
  // attempt on. The Dashboard uses this to hide assessments the
  // candidate has already sat so they're not offered a second
  // sitting — mirrors the stricter server-side startAttempt guard.
  taken_assessment_ids: string[];
}

// The selectable headline window the org/teacher stats KPIs reflect.
// `days: null` means an unbounded "all time" window.
export interface StatsWindow {
  days: number | null;
  label: string;
}

// The period a caller can request for the windowed KPIs. "30" is the default
// when omitted, so the Stats selector passes one of these three explicitly.
export type StatsPeriod = "30" | "90" | "all";

export interface TeacherDashboard {
  totals: {
    published_assessments: number;
    total_attempts: number;
    // The selected window's submitted-attempt count + the window it reflects.
    // `attempts_30d` stays a fixed 30-day figure for the dashboard widget.
    attempts_in_window: number;
    window: StatsWindow;
    attempts_30d: number;
    awaiting_review: number;
  };
  by_assessment: Array<{
    assessment_id: string;
    title: string;
    module: string;
    level_code: string;
    pass_threshold_percent: number;
    // raw_score papers have no verdict, so pass_rate_percent is null
    // for them — render the score distribution only.
    grading_mode: "pass_fail" | "raw_score";
    attempts_count: number;
    unique_students: number;
    scored_count: number;
    awaiting_review_count: number;
    avg_score_percent: number | null;
    pass_rate_percent: number | null;
  }>;
  recent_submissions: Array<{
    attempt_id: string;
    assessment_id: string;
    assessment_title: string;
    module: string;
    level_code: string;
    student_user_id: string;
    student_email: string | null;
    student_full_name: string | null;
    status: string;
    score_percent: number | null;
    passed: boolean | null;
    band: string | null;
    score_reporting: "percent" | "band";
    grading_mode: "pass_fail" | "raw_score";
    submitted_at: string | null;
  }>;
}

export interface OrgDashboard {
  headline: {
    published_assessments: number;
    total_attempts: number;
    // Windowed KPIs for the selected period + the resolved window so the UI
    // can label the headline. `attempts_30d` / `active_students_30d` stay as
    // fixed 30-day figures for the dashboard widget.
    attempts_in_window: number;
    active_students_in_window: number;
    window: StatsWindow;
    attempts_30d: number;
    awaiting_review: number;
    active_students_30d: number;
  };
  by_level_module: Array<{
    level_code: string;
    module: string;
    attempts: number;
    passed: number;
    avg_score_percent: number | null;
    pass_rate_percent: number | null;
  }>;
  recent_audit: Array<{
    id: string;
    actor_user_id: string | null;
    // Resolved from the master users table; null for deleted users or a
    // system (null actor) row.
    actor_name: string | null;
    actor_email: string | null;
    action: string;
    target_type: string | null;
    target_id: string | null;
    created_at: string;
  }>;
}

export interface MarkingQueueItem {
  attempt_id: string;
  assessment_id: string;
  assessment_title: string;
  module: string;
  student_user_id: string;
  // Resolved candidate identity from the master users table; null when the
  // user couldn't be resolved (deleted) — fall back to the short id.
  student_full_name: string | null;
  student_email: string | null;
  submitted_at: string;
  responses_count: number;
}

export interface AgreementSummary {
  threshold_pp: number;
  all_within_threshold: boolean;
  mean_delta_pp: number;
  max_delta_pp: number;
  responses: Array<{
    response_id: string;
    question_marks: number;
    first_marks: number;
    second_marks: number;
    delta_pp: number;
    within_threshold: boolean;
    averaged_marks: number;
  }>;
}

export interface ModerationDetail {
  attempt: MarkingAttemptDetail;
  first_session: {
    id: string;
    kind: "first";
    marker_user_id: string;
    score_percent: number | null;
  };
  second_session: {
    id: string;
    kind: "second";
    marker_user_id: string;
    score_percent: number | null;
  };
  first_totals: Array<{
    response_id: string;
    question_marks: number;
    raw: number;
  }>;
  second_totals: Array<{
    response_id: string;
    question_marks: number;
    raw: number;
  }>;
}

// Examiner-side session-recording manifest (tenant v26). The API concatenates
// the gzipped rrweb event chunks back into a single replayable stream in seq
// order; `available:false` means the attempt was never recorded (or the row
// was purged after the 12-month retention window). `events`/`session_meta`/
// `stats` are intentionally loosely typed — they are rrweb's own opaque shapes
// plus the small action-count object the runner writes on finalize.
export interface RecordingManifest {
  available: boolean;
  events?: unknown[];
  session_meta?: unknown;
  stats?: unknown;
  consent_at?: string;
  finalized_at?: string | null;
}

// ── Session replays (owner console, tenant v26) ──────────────────────────────
export interface ReplayIntegrity {
  clicks: number;
  text_inputs: number;
  rage_clicks: number;
  tab_aways: number;
  errors: number;
}

export interface ReplayListRow {
  attempt_id: string;
  candidate: { user_id: string; name: string | null; email: string | null };
  assessment: { id: string; title: string; module: string; level_code: string };
  sitting_id: string | null;
  status: string;
  started_at: string;
  finalized_at: string | null;
  duration_ms: number | null;
  events: number;
  integrity: ReplayIntegrity;
  chunk_count: number;
  available: boolean;
}

export interface ReplayListResult {
  rows: ReplayListRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ReplaySummary {
  total: number;
  flagged: number;
  sum_tab_aways: number;
  sum_rage_clicks: number;
  sum_errors: number;
  avg_duration_ms: number;
}

export interface ReplaySessionMeta {
  device?: string;
  browser?: string;
  os?: string;
  ip_country?: string;
  screen_w?: number;
  screen_h?: number;
  viewport_w?: number;
  viewport_h?: number;
  tz?: string;
  ua?: string;
}

export type ReplayDetail =
  | { available: false }
  | {
      available: true;
      attempt_id: string;
      candidate: { user_id: string; name: string | null; email: string | null };
      assessment: { id: string; title: string; module: string; level_code: string };
      sitting_id: string | null;
      status: string;
      chunk_count: number;
      started_at: string;
      finalized_at: string | null;
      consent_at: string;
      consent_text: string;
      session_meta: ReplaySessionMeta | null;
      stats: ReplayIntegrity & { duration_ms?: number; events?: number };
    };

export interface MarkingAttemptDetail {
  attempt_id: string;
  assessment: {
    id: string;
    title: string;
    instructions: string;
    module: string;
    level_code: string;
    pass_threshold_percent: number;
    grading_mode: "pass_fail" | "raw_score";
    score_reporting: "percent" | "band";
    band_boundaries_json: string | null;
  };
  student_user_id: string;
  // Resolved candidate identity from the master users table; null when the
  // user couldn't be resolved (deleted) — fall back to the short id.
  student_full_name: string | null;
  student_email: string | null;
  submitted_at: string | null;
  time_taken_seconds: number | null;
  status: string;
  rubric: {
    id: string;
    name: string;
    scale_max: number;
    criteria: Array<{
      id: string;
      name: string;
      description: string;
      weight: number;
    }>;
  } | null;
  responses: Array<{
    id: string;
    question_id: string;
    question_prompt: string;
    question_marks: number;
    question_type: string;
    question_metadata: Record<string, unknown>;
    section_title: string;
    answer_text: string | null;
    word_count: number | null;
    answer_r2_key: string | null;
    audio_duration_seconds: number | null;
    manual_score: number | null;
    feedback: string | null;
    rubric_scores: Array<{
      criterion_id: string;
      score: number;
      comment: string | null;
    }>;
    transcription: TranscriptionResult | null;
  }>;
}

export interface TranscriptionResult {
  model: string;
  ok: boolean;
  text: string;
  word_count: number | null;
  created_at: string;
  error?: string;
}

export interface QuestionUpdateBatch {
  id: string;
  assessment_id: string;
  status: string; // pending | applied | failed | rolled_back
  question_count: number;
  result: Array<{ id: string; status: string; error?: string }> | null;
  created_at: string;
  applied_at: string | null;
  rolled_back_at: string | null;
}

export interface AssessmentTemplateSummary {
  source_org_id: string;
  source_org_name: string;
  assessment_id: string;
  title: string;
  module: string;
  level_code: string;
  programme_id: string | null;
  section_count: number;
  question_count: number;
}

export interface AssessmentSummary {
  id: string;
  title: string;
  module: string;
  level_code: string;
  duration_seconds: number;
  pass_threshold_percent: number;
  grading_mode?: "pass_fail" | "raw_score";
  score_reporting?: "percent" | "band";
  opens_at?: string | null;
  closes_at?: string | null;
  max_attempts: number;
  status: string;
  course_id: string | null;
  programme_id: string | null;
  is_template: number;
  created_at: string;
  // Aggregate counts populated by the list endpoint — used by
  // AuthoringList to show "3 sections · 12 Qs" inline. Optional so
  // older deployments / cached responses still parse.
  section_count?: number;
  question_count?: number;
}

export interface TenantBranding {
  logo_r2_key: string | null;
  primary_hsl: string | null;
  accent_hsl: string | null;
  footer_text: string | null;
  organisation_name: string | null;
}

export interface OrgOpsSettings {
  // Examiners with more than this many open marking sessions are flagged on
  // the marking-workload page. Defaults to 10 (the historical hardcoded value).
  marking_sla_threshold: number;
}

export interface TestCentre {
  id: string;
  name: string;
  // Always present from the API (empty string when unset); optional here for
  // back-compat with older payloads / test fixtures.
  address?: string;
}

export interface CentreRoomCode {
  centre_id: string;
  centre_name: string;
  centre_address: string;
  code: string;
  /** ISO instant the code last changed; null when it never has. */
  code_updated_at: string | null;
  /** True when this centre has a code set, and so gates candidate entry. */
  enforced: boolean;
}

export interface TestCentresConfig {
  centres: TestCentre[];
  default_id: string | null;
}

export interface OrgMember {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  role: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
  is_examiner: boolean;
  // Founder = the user who created the organisation via /register. Their
  // role + status are immutable from the tenant-admin UI so the tenant
  // always retains at least one privileged admin.
  is_founder: boolean;
  status: "active" | "invited" | "suspended";
  joined_at: string;
  last_login_at: string | null;
}

// A pending (unaccepted, unexpired) invite. No user_id yet — the account is
// minted only when the invite is accepted.
export interface PendingInvite {
  id: string;
  email: string;
  role: "tenant_admin" | "moderator" | "teacher" | "registrar" | "student";
  is_examiner: boolean;
  expires_at: string;
  created_at: string;
  invited_by_name: string | null;
}

export interface CandidateSlipSummary {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  access_code: string | null;
  access_code_last4: string;
  role: "student" | "teacher" | "moderator";
  // Unique per-org candidate number, the source for the printed "Slip no.".
  // Null only on legacy slips minted before the counter existed.
  candidate_no: number | null;
  assessment_id: string | null;
  sitting_id: string | null;
  exam_centre: string | null;
  // Venue address captured on the slip (name is exam_centre). Optional for
  // back-compat with older payloads.
  exam_centre_address?: string | null;
  exam_date: string | null;
  exam_time: string | null;
  status: "active" | "used" | "revoked" | "expired" | "locked";
  uses: number;
  max_uses: number;
  failed_attempts: number;
  expires_at: string;
  revoked_at: string | null;
  created_at: string;
  last_used_at: string | null;
  // Whether a candidate photo is attached (derived server-side from
  // photo_r2_key; the raw key is never exposed). Optional for back-compat
  // with any cached/older payloads.
  has_photo?: boolean;
  // Real delivery address for certificates/results (never the synthetic
  // account key). null when unset. Optional for back-compat with older payloads.
  contact_email?: string | null;
  // Test-case slip: excluded from billing/metering and real sends. Optional
  // for back-compat with older payloads (treat absent as false).
  is_test?: boolean;
}

export interface OwnSession {
  id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
}

export interface PlatformAuditEvent {
  id: string;
  actor_user_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata_json: string;
  ip: string | null;
  created_at: string;
}

export interface StudentAssessment {
  id: string;
  title: string;
  instructions: string;
  module: string;
  level_code: string;
  duration_seconds: number;
  /** Paper version. Surfaced so the TestRunner header can show
   *  "Paper v3" — useful when sittings run multiple versions of the
   *  same paper in parallel, so invigilators/markers can correlate. */
  version?: number;
  grading_mode?: "pass_fail" | "raw_score";
  // Author flag — when true the TestRunner bypasses the 3-step
  // identity/rules/honour-code preflight and drops the candidate
  // straight into the questions.
  skip_preflight?: boolean;
  // Per-assessment AI grammar-check gate (tenant v25). When false/absent (the
  // DEFAULT) the writing answer hides the "Check grammar" button so a
  // SPaG-assessed paper can't leak corrections; the API route enforces the
  // same flag regardless of the client.
  allow_grammar_check?: boolean;
  // Per-assessment copy/paste gate (tenant v37). When true the runner leaves
  // copy/paste/context-menu alone: no block, no integrity incident.
  allow_copy_paste?: boolean;
  // Per-assessment session-recording gate (tenant v26). When false/absent (the
  // DEFAULT) the runner shows no consent gate and never imports rrweb; when
  // true the candidate must consent before Begin and the session is recorded.
  record_session?: boolean;
  // Mock (practice) paper (tenant v32). When true the runner shows a
  // persistent "practice — does not count" banner. The paper is sat and marked
  // exactly like a real one; it simply never becomes evidence.
  is_mock?: boolean;
  sections: Array<{
    id: string;
    order_index: number;
    title: string;
    instructions: string;
    stimulus_type: string;
    stimulus_text: string;
    stimulus_r2_key: string | null;
    stimulus_content_type: string | null;
    stimulus_duration_seconds: number | null;
    /** First-class SHARED stimulus image (tenant v20). Content hash served
     *  read-only by capability via /v1/question-assets/<hash>; null/absent
     *  when the section has no first-class image. */
    stimulus_image_hash?: string | null;
    /** First-class SHARED stimulus video (tenant v22). Content hash served
     *  read-only by capability via /v1/question-assets/<hash>; rendered as
     *  <video> by the TestRunner. null/absent when the section has no video. */
    stimulus_video_hash?: string | null;
    /** WCAG 1.1.1 alt text for the section's stimulus image/audio. Always ""
     *  for an audio section: there the column holds the recording transcript,
     *  which is the content under test. */
    alt_text?: string;
    /** Playback clip window (tenant v34). The listening player starts at
     *  clip_start_seconds and stops at clip_end_seconds, so IELTS rubric baked
     *  into the head/tail of a sourced recording is never heard. Both null (the
     *  common case) means play the whole file. Positions are FILE offsets; the
     *  UI renders everything clip-relative. */
    clip_start_seconds?: number | null;
    clip_end_seconds?: number | null;
    max_replays: number | null;
    replays_used: number;
    /** Last paused playback offset in seconds (tenant v24). The Listening
     *  player resumes from here (minus a small grace) instead of restarting —
     *  without claiming a new replay. 0/absent when never played. */
    position_seconds?: number;
    /** True once the audio reached the end (tenant v24); the next play is then
     *  a genuinely fresh play that claims a new replay. */
    finished?: boolean;
    time_limit_seconds: number | null;
    questions: Array<{
      id: string;
      order_index: number;
      type:
        | "mcq_single"
        | "mcq_multi"
        | "true_false"
        | "short_answer"
        | "long_answer"
        | "essay"
        | "speaking_response"
        | "numeric_answer"
        | "match_pairs"
        | "multi_part";
      prompt: string;
      marks: number;
      metadata: Record<string, unknown>;
      // pair_side is populated only for match_pairs options (0 = left/stimulus,
      // 1 = right/match); absent otherwise. pair_group is the answer key and is
      // NEVER sent to the student — the widget splits/orders by pair_side +
      // order_index, and the server marks pair_group-side from the DB.
      // image_hash is option CONTENT (a match_pairs item can be a picture),
      // served read-only by capability via /v1/question-assets/<hash>; null/
      // absent for text-only options. Safe to ship — reveals no pairing.
      options: Array<{
        id: string;
        order_index: number;
        text: string;
        question_id: string;
        pair_side?: number | null;
        image_hash?: string | null;
      }>;
    }>;
  }>;
}

export interface SubmitResult {
  attempt_id: string;
  status: string;
  // Nulled when the candidate may not see the score — either candidate_can_see_score
  // is off or the attempt is still provisionally marked (awaiting_review). The
  // submit payload now mirrors getAttemptResult's disclosure rules so the result
  // page never falls back to show-everything defaults on a freshly-cached submit.
  score_raw: number | null;
  score_max: number | null;
  score_percent: number | null;
  // null when the assessment's grading_mode is 'raw_score' — IELTS-
  // style papers return just the score without a pass/fail verdict — or hidden.
  passed: boolean | null;
  // Band label looked up from the assessment's boundaries at
  // submit time. Null in percent-reporting mode or when hidden.
  band?: string | null;
  // Which of band/percent the assessment reports in, so the result
  // page can render the right headline without a separate fetch.
  score_reporting?: "percent" | "band";
  time_taken_seconds: number;
  // Non-null once an admin has released a manual_hold assessment; null on immediate.
  released_at?: string | null;
  // What the candidate is allowed to see. Present on every submit response.
  disclosure: AttemptResultDisclosure;
  per_question: Array<{
    question_id: string;
    awarded: number;
    possible: number;
    // null when show_correct_answers is off.
    is_correct: boolean | null;
  }>;
}

export interface AttemptResultDisclosure {
  show_score: boolean;
  show_per_question: boolean;
  show_correct_answers: boolean;
  show_feedback: boolean;
}

export interface AttemptResult {
  attempt_id: string;
  assessment_id: string;
  status: string;
  score_raw: number | null;
  score_max: number | null;
  score_percent: number | null;
  passed: boolean | null;
  submitted_at: string | null;
  time_taken_seconds: number | null;
  // Non-null once an admin has flipped results_released_at on a
  // manual-hold assessment. Null when the result was never held.
  released_at?: string | null;
  // Frozen at submit time when the parent assessment is in band
  // reporting mode. Null in percent mode or when the boundary
  // lookup failed.
  band?: string | null;
  score_reporting?: "percent" | "band";
  // Which sections of the result page are visible to the candidate.
  // Optional for back-compat; default is "show everything".
  disclosure?: AttemptResultDisclosure;
  // Paper metadata for the cert PDF's metadata strip. Optional for
  // back-compat with pre-v2 responses.
  pass_threshold_percent?: number;
  assessment_version?: number;
  /** Mock (practice) paper. Sourced from the server rather than only the
   *  TestRunner's sessionStorage bootstrap, so the practice notice survives a
   *  fresh tab, a deep link, or a resume-from-server landing. */
  is_mock?: boolean;
  // Prior attempts by this candidate on the same paper; empty on
  // first sit. Most recent first.
  prior_attempts?: Array<{
    attempt_id: string;
    status: string;
    score_percent: number | null;
    passed: boolean | null;
    band: string | null;
    submitted_at: string | null;
    // Per-row reporting/grading so each prior attempt's Score cell
    // renders in ITS OWN mode (band label vs percent) rather than the
    // current attempt's. Optional for back-compat with pre-projection
    // responses.
    score_reporting?: "percent" | "band";
    grading_mode?: "pass_fail" | "raw_score";
  }>;
  responses: Array<{
    question_id: string;
    awarded: number;
    // The question's max marks, so the page renders an honest
    // "awarded/possible" denominator. Null when the question has no
    // max on record — show awarded marks only in that case.
    possible: number | null;
    is_correct: boolean | null;
    feedback: string | null;
  }>;
}

// Served instead of AttemptResult when the assessment is in
// manual-hold release mode and results_released_at is still null.
export interface AttemptResultHeld {
  status: "held";
  attempt_id: string;
  assessment_id: string;
  submitted_at: string | null;
  released_at: null;
}
